import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { OverviewDto, OverviewGroupDto, OverviewRowDto } from '@models/job-request-overview-dto.interface';
import { AttachmentDto } from '@models/job-detail-dto.interface';
import { TestInputService, UpdateTestInputDto } from 'src/app/services/test-input.service';
import { TestOutputService, UpdateTestOutputDto } from 'src/app/services/test-output.service';
import { PycnometerProfileSummary } from '@models/pycnometer-profile.interface';
import { PycnometerProfilesService } from 'src/app/services/pycnometer-profiles.service';
import { JobRequestsService } from 'src/app/services/job-requests.service';
import { DesignService } from 'src/app/services/design.service';
import { AttachmentService } from 'src/app/services/attachment.service';
import { SystemSettingsService } from 'src/app/services/system-settings.service';
import { AttachmentType } from '@enums/attachment-type.enum';
import { TestTypeLookupDto } from '@models/test-type-lookup.interface';
import { JobRequestTestPycnometerDetailComponent } from './test-details/job-request-test-pycnometer-detail.component';
import { JobRequestTestDensityDetailComponent } from './test-details/job-request-test-density-detail.component';
import { JobRequestTestRheologyDetailComponent } from './test-details/job-request-test-rheology-detail.component';
import { JobRequestTestRetortDetailComponent } from './test-details/job-request-test-retort-detail.component';
import {
  calculateNafChemistryTitration,
  calculateNafCorrectedSolidsVolumePercent,
  calculateNafSolidsComposition,
  calculateNafWaterPhaseSalinity
} from '../../../shared/utils/naf-chemistry-calculations';
import { JobRequestTestElectricalStabilityDetailComponent } from './test-details/job-request-test-electrical-stability-detail.component';
import { JobRequestTestHphtFluidLossDetailComponent } from './test-details/job-request-test-hpht-fluid-loss-detail.component';
import { JobRequestTestPptDetailComponent } from './test-details/job-request-test-ppt-detail.component';
import { JobRequestTestPsdDetailComponent } from './test-details/job-request-test-psd-detail.component';
import { JobRequestTestApiFluidLossDetailComponent } from './test-details/job-request-test-api-fluid-loss-detail.component';
import { JobRequestTestWbmTitrationDetailComponent } from './test-details/job-request-test-wbm-titration-detail.component';
import { JobRequestTestNafChemistryDetailComponent } from './test-details/job-request-test-naf-chemistry-detail.component';
import { JobRequestTestWbmSalinityDetailComponent } from './test-details/job-request-test-wbm-salinity-detail.component';
import { JobRequestTestStaticAgingDetailComponent } from './test-details/job-request-test-static-aging-detail.component';
import { JobRequestTestHphtRheologyDetailComponent, HphtRheologyReadingView, HphtRheologyRowView, Fann35ReferenceView } from './test-details/job-request-test-hpht-rheology-detail.component';
import { JobRequestTestCustomDetailComponent } from './test-details/job-request-test-custom-detail.component';
import { SpreadsheetService } from '../../../shared/components/spreadsheet/spreadsheet.service';
import { CellDefinition, Worksheet } from '../../../shared/components/spreadsheet/spreadsheet.models';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
// ── Custom-test formula evaluator (module-level, no eval) ─────────────────

function escapeRegExpForFormula(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function evalFormulaArithmetic(expr: string): number {
  let pos = 0;

  function skipWs(): void { while (pos < expr.length && expr[pos] === ' ') pos++; }

  function parseExpr(): number {
    let left = parseTerm(); skipWs();
    while (pos < expr.length && (expr[pos] === '+' || expr[pos] === '-')) {
      const op = expr[pos++];
      left = op === '+' ? left + parseTerm() : left - parseTerm();
      skipWs();
    }
    return left;
  }

  function parseTerm(): number {
    let left = parseUnary(); skipWs();
    while (pos < expr.length && (expr[pos] === '*' || expr[pos] === '/' || expr[pos] === '%')) {
      const op = expr[pos++];
      const right = parseUnary();
      if (op === '*') left *= right;
      else if (op === '/') left = right !== 0 ? left / right : NaN;
      else left %= right;
      skipWs();
    }
    return left;
  }

  function parseUnary(): number {
    skipWs();
    if (pos < expr.length && expr[pos] === '-') { pos++; return -parsePrimary(); }
    if (pos < expr.length && expr[pos] === '+') { pos++; }
    return parsePrimary();
  }

  function parsePrimary(): number {
    skipWs();
    if (pos < expr.length && expr[pos] === '(') {
      pos++;
      const val = parseExpr();
      skipWs();
      if (pos < expr.length && expr[pos] === ')') pos++;
      return val;
    }
    let numStr = '';
    while (pos < expr.length && /[0-9.]/.test(expr[pos])) numStr += expr[pos++];
    skipWs();
    return numStr ? parseFloat(numStr) : NaN;
  }

  return parseExpr();
}

function evaluateCustomFormula(formula: string, inputValues: Record<string, string>): string {
  const sortedNames = Object.keys(inputValues).sort((a, b) => b.length - a.length);
  let expr = formula;
  for (const name of sortedNames) {
    const numVal = parseFloat(inputValues[name]);
    if (!isNaN(numVal)) {
      expr = expr.replace(new RegExp(`\\b${escapeRegExpForFormula(name)}\\b`, 'g'), String(numVal));
    }
  }
  if (/[A-Za-z_]/.test(expr)) return '';
  try {
    const result = evalFormulaArithmetic(expr.replace(/\s+/g, ''));
    return isFinite(result) ? result.toFixed(5) : '';
  } catch {
    return '';
  }
}


type TestDetailFieldType = 'number' | 'text' | 'select' | 'computed';

interface TestDetailField {
  key: string;
  label: string;
  type: TestDetailFieldType;
  min?: number;
  step?: number;
  options?: string[];
}

interface TestDetailSection {
  title: string;
  fields: TestDetailField[];
}

interface RheologyRow {
  key: string;
  label: string;
  type: 'number';
  min?: number;
  step?: number;
}

interface ElectricalStabilityReadingState {
  index: number;
  temperature: number;
}

interface AddTestReadingInput {
  temperature: string;
  pressure: string;
}

interface MudCheckSpecificGravityCandidate {
  testKey: string;
  name: 'Density' | 'Pycnometer';
}

interface MudCheckSpecificGravityResult {
  specificGravity: number;
  duplicateNames: Array<'Density' | 'Pycnometer'>;
}

const TEST_DETAIL_SCHEMAS: Record<string, TestDetailSection[]> = {
  density: [
    {
      title: 'Density',
      fields: [
        { key: 'specificGravity', label: 'Specific Gravity', type: 'number', min: 0, step: 0.001 },
        { key: 'densityPpg', label: 'Density (lb/gal)', type: 'computed' }
      ]
    }
  ],
  retort: [
    {
      title: 'Retort Input',
      fields: [
        { key: 'sampleVolumeMl', label: 'Sample Volume (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'cellMudWeight', label: 'Cell + Mud (g)', type: 'number', min: 0, step: 0.1 },
        { key: 'cellTareWeight', label: 'Cell Tare (g)', type: 'number', min: 0, step: 0.1 },
        { key: 'cellAfterWeight', label: 'Cell After (g)', type: 'number', min: 0, step: 0.1 },
        { key: 'totalVolume', label: 'Total Vol (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'waterVolume', label: 'Water Vol (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'jTubeGrossWeight', label: 'J-Tube Gross (g)', type: 'number', min: 0, step: 0.1 },
        { key: 'jTubeTareWeight', label: 'J-Tube Tare (g)', type: 'number', min: 0, step: 0.1 },
        { key: 'equipmentName', label: 'Equipment Name', type: 'text' }
      ]
    }
  ],
  electricalStability: [
    {
      title: 'Electrical Stability',
      fields: [
        { key: 'requestedTemperature', label: 'Requested Temperature (degF)', type: 'number', step: 1 },
        { key: 'voltage1', label: 'Dielectric Voltage #1', type: 'number', min: 0, step: 1 },
        { key: 'voltage2', label: 'Dielectric Voltage #2', type: 'number', min: 0, step: 1 },
        { key: 'voltage3', label: 'Dielectric Voltage #3', type: 'number', min: 0, step: 1 },
        { key: 'averageVoltage', label: 'Average Voltage', type: 'computed' },
        { key: 'equipmentName', label: 'Equipment Name', type: 'text' }
      ]
    }
  ],
  hphtFluidLoss: [
    {
      title: 'HPHT Fluid Loss',
      fields: [
        { key: 'temperature', label: 'Test Temp (F)', type: 'number', step: 1 },
        { key: 'pressure', label: 'Diff. Pressure (psi)', type: 'number', step: 1 },
        { key: 'filtrate30Min', label: 'HTHP Filtrate (ml)', type: 'number', min: 0, step: 0.001 },
        { key: 'waterVolume', label: 'Water (ml)', type: 'number', min: 0, step: 0.001 },
        { key: 'cakeThickness', label: 'HTHP Cake (32nd)', type: 'number', min: 0, step: 1 },
        { key: 'equipmentName', label: 'Equipment Name', type: 'text' }
      ]
    }
  ],
  ppt: [
    {
      title: 'Permeability Plugging Test',
      fields: [
        { key: 'discGrade', label: 'Disc Grade', type: 'text' },
        { key: 'temperature', label: 'Test Temp (degF)', type: 'number', step: 1 },
        { key: 'pressure', label: 'Diff. pressure (psi)', type: 'number', step: 1 },
        { key: 'filtrateVolume', label: 'Filtrate Volume (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'totalVolume', label: 'Total Volume (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'cakeThickness', label: 'PPT Cake (32nd)', type: 'number', min: 0, step: 1 },
        { key: 'equipmentName', label: 'Equipment Name', type: 'text' }
      ]
    }
  ],
  psd: [
    {
      title: 'Particle Size Distribution',
      fields: [
        { key: 'percentile5', label: 'Percentile 5', type: 'number', min: 0, step: 0.1 },
        { key: 'percentile10', label: 'Percentile 10', type: 'number', min: 0, step: 0.1 },
        { key: 'percentile25', label: 'Percentile 25', type: 'number', min: 0, step: 0.1 },
        { key: 'percentile40', label: 'Percentile 40', type: 'number', min: 0, step: 0.1 },
        { key: 'percentile50', label: 'Percentile 50', type: 'number', min: 0, step: 0.1 },
        { key: 'percentile60', label: 'Percentile 60', type: 'number', min: 0, step: 0.1 },
        { key: 'percentile75', label: 'Percentile 75', type: 'number', min: 0, step: 0.1 },
        { key: 'percentile85', label: 'Percentile 85', type: 'number', min: 0, step: 0.1 },
        { key: 'percentile90', label: 'Percentile 90', type: 'number', min: 0, step: 0.1 },
        { key: 'percentile95', label: 'Percentile 95', type: 'number', min: 0, step: 0.1 },
        { key: 'percentile5_size', label: '5% less than', type: 'number', min: 0, step: 0.1 },
        { key: 'percentile10_size', label: '10% less than', type: 'number', min: 0, step: 0.1 },
        { key: 'percentile25_size', label: '25% less than', type: 'number', min: 0, step: 0.1 },
        { key: 'percentile40_size', label: '40% less than', type: 'number', min: 0, step: 0.1 },
        { key: 'percentile50_size', label: '50% less than', type: 'number', min: 0, step: 0.1 },
        { key: 'percentile60_size', label: '60% less than', type: 'number', min: 0, step: 0.1 },
        { key: 'percentile75_size', label: '75% less than', type: 'number', min: 0, step: 0.1 },
        { key: 'percentile85_size', label: '85% less than', type: 'number', min: 0, step: 0.1 },
        { key: 'percentile90_size', label: '90% less than', type: 'number', min: 0, step: 0.1 },
        { key: 'percentile95_size', label: '95% less than', type: 'number', min: 0, step: 0.1 },
        { key: 'diameter6', label: 'Diameter 6 micron', type: 'number', min: 0, step: 0.1 },
        { key: 'diameter44', label: 'Diameter 44 micron', type: 'number', min: 0, step: 0.1 },
        { key: 'diameter74', label: 'Diameter 74 micron', type: 'number', min: 0, step: 0.1 },
        { key: 'size6_percentile', label: '6 micron cumulative %', type: 'number', min: 0, step: 0.1 },
        { key: 'size44_percentile', label: '44 micron cumulative %', type: 'number', min: 0, step: 0.1 },
        { key: 'size74_percentile', label: '74 micron cumulative %', type: 'number', min: 0, step: 0.1 },
        { key: 'equipmentName', label: 'Equipment Name', type: 'text' }
      ]
    }
  ],
  apiFluidLoss: [
    {
      title: 'API Fluid Loss',
      fields: [
        { key: 'filtrateVolumeLimit', label: 'Limit of Filtrate Volume (ml)', type: 'number', min: 0, step: 1 },
        { key: 'filtrateVolume', label: 'Filtrate Volume', type: 'number', min: 0, step: 0.1 },
        { key: 'cakeThickness', label: 'HTHP Cake (32nd)', type: 'number', min: 0, step: 1 },
        { key: 'equipmentName', label: 'Equipment Name', type: 'text' }
      ]
    }
  ],
  wbmTitration: [
    {
      title: 'S3/S4 WBM Titration',
      fields: [
        { key: 'mudSpecificGravity', label: 'Mud Density (g/ml)', type: 'computed' },
        { key: 'mudDensityPpg', label: 'Mud Density (lb/gal)', type: 'computed' },
        { key: 'mudSampleWeight', label: 'Mud Sample Weight (g)', type: 'number', min: 0, step: 0.1 },
        { key: 'mudSampleVolume', label: 'Mud Sample Volume (ml)', type: 'computed' },
        { key: 'diluentTitratedVolume', label: 'Amt diluent titrated (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'flaskVolume', label: 'Size of volumetr.flask (ml)', type: 'number', min: 0, step: 1 },
        { key: 'agNo3Volume', label: 'Amt of AgNO3 (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'agNo3Concentration', label: 'AgNO3 Concentration', type: 'select', options: ['10000', '1000'] },
        { key: 'chloridesInBlend', label: 'Cl in blend (g/ml)', type: 'computed' },
        { key: 'bariteLbPerBbl', label: 'Barite (lb/bbl)', type: 'number', min: 0, step: 0.1 },
        { key: 'bariteShareGg', label: 'Barite (g/g)', type: 'computed' },
        { key: 'bariteVolumeShare', label: 'Barite volume share (ml/ml)', type: 'computed' },
        { key: 'bariteDensity', label: 'Barite Density (g/ml)', type: 'computed' },
        { key: 'bariteInFlaskWeight', label: 'Barite in Flask (g)', type: 'computed' },
        { key: 'bariteInFlaskVolume', label: 'Barite in Flask (ml)', type: 'computed' },
        { key: 'volumeMinusBarite', label: 'Volume minus Barite (ml)', type: 'computed' },
        { key: 'dilutionRatio', label: 'Dilution Ratio', type: 'computed' },
        { key: 'totalChloridesGml', label: 'Total Chlorides (g/ml)', type: 'computed' },
        { key: 'edtaVolume', label: 'Amt of EDTA (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'acidVolume', label: 'Amt of H2SO4 (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'equipmentName', label: 'Equipment Name', type: 'text' }
      ]
    }
  ],
  salinity: [
    {
      title: 'Salinity',
      fields: [
        { key: 'alkalineDemand', label: 'Basic Alk. Demand (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'mudUsedVolume', label: 'Amt of Mud Used (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'acidVolume', label: 'Amt. of H2SO4 (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'agNo3Volume', label: 'Amt of AgNO3 (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'edtaVolume', label: 'Amt. of EDTA (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'equipmentName', label: 'Equipment Name', type: 'text' }
      ]
    }
  ],
  wbmSalinity: [
    {
      title: 'WBM Chemistry',
      fields: [
        { key: 'wholeMudSampleVolume', label: 'Whole Mud Sample Volume (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'wholeMudSilverNitrateVolume', label: 'Whole Mud AgNO3 (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'wholeMudAcidVolume', label: 'Whole Mud H2SO4 (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'wholeMudEdtaVolume', label: 'Whole Mud EDTA (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'wholeMudSilverNitrateConcentration', label: 'Whole Mud AgNO3 Concentration', type: 'select', options: ['10000', '1000'] },
        { key: 'wholeMudEdtaConcentration', label: 'Whole Mud EDTA Concentration', type: 'select', options: ['4000', '400'] },
        { key: 'filtrateSampleVolume', label: 'Filtrate Sample Volume (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'filtrateSilverNitrateVolume', label: 'Filtrate AgNO3 (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'filtrateSilverNitrateConcentration', label: 'Filtrate AgNO3 Concentration', type: 'select', options: ['10000', '1000'] },
        { key: 'filtrateEdtaTotalHardnessVolume', label: 'Filtrate EDTA Total Hardness (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'filtrateEdtaCalciumVolume', label: 'Filtrate EDTA Calcium (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'filtrateEdtaConcentration', label: 'Filtrate EDTA Concentration', type: 'select', options: ['4000', '400'] },
        { key: 'filtrateAcidPfVolume', label: 'Filtrate H2SO4 Pf (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'filtrateAcidMfVolume', label: 'Filtrate H2SO4 Mf (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'filtrateMethyleneBlueDemandVolume', label: 'Filtrate Methylene Blue Demand (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'filtrateMethyleneBlueSampleVolume', label: 'Filtrate Methylene Blue Sample Volume (ml)', type: 'number', min: 0, step: 0.1 },
        { key: 'sandContentPercentByVolume', label: 'Sand Content (% vol)', type: 'number', min: 0, step: 0.1 },
        { key: 'ph', label: 'pH', type: 'number', min: 0, step: 0.1 },
        { key: 'equipmentName', label: 'Equipment Name', type: 'text' }
      ]
    }
  ],
  staticAging: [
    {
      title: 'Static Aging',
      fields: [
        { key: 'temperature', label: 'Temperature (degF)', type: 'number', step: 1 },
        { key: 'pressure', label: 'Pressure (psi)', type: 'number', step: 1 },
        { key: 'duration', label: 'Duration', type: 'text' }
      ]
    },
    {
      title: 'Input',
      fields: [
        { key: 'staticAging_mudDensityPpg', label: 'Mud Density (lb/gal)', type: 'computed' },
        { key: 'mudVolume', label: 'Mud Vol (ml)', type: 'number', min: 0, step: 0.1 }
      ]
    },
    {
      title: 'Measurements',
      fields: [
        { key: 'columnMass', label: 'Column (g)', type: 'number', min: 0, step: 0.01 },
        { key: 'shelfMass', label: 'Shelf (g)', type: 'number', min: 0, step: 0.01 },
        { key: 'weightsMass', label: 'Weights (g)', type: 'number', min: 0, step: 0.01 },
        { key: 'penetration', label: 'Penetration (in)', type: 'number', min: 0, step: 0.001 },
        { key: 'freeFluid', label: 'Free Fluid (ml)', type: 'number', min: 0, step: 0.1 }
      ]
    },
    {
      title: 'Density',
      fields: [
        { key: 'staticAging_topDensity', label: 'Top Density (lb/gal)', type: 'computed' },
        { key: 'staticAging_middleDensity', label: 'Middle Density (lb/gal)', type: 'computed' },
        { key: 'staticAging_bottomDensity', label: 'Bottom Density (lb/gal)', type: 'computed' },
        { key: 'staticAging_asaCombinedDensity', label: 'Combined Density (lb/gal)', type: 'computed' }
      ]
    },
    {
      title: 'Calculated Values',
      fields: []
    }
  ],
  hphtRheology: []
};

@Component({
  selector: 'app-job-request-overview',
  standalone: true,
  imports: [
    CommonModule,
    JobRequestTestPycnometerDetailComponent,
    JobRequestTestDensityDetailComponent,
    JobRequestTestRheologyDetailComponent,
    JobRequestTestRetortDetailComponent,
    JobRequestTestElectricalStabilityDetailComponent,
    JobRequestTestHphtFluidLossDetailComponent,
    JobRequestTestPptDetailComponent,
    JobRequestTestPsdDetailComponent,
    JobRequestTestApiFluidLossDetailComponent,
    JobRequestTestWbmTitrationDetailComponent,
    JobRequestTestNafChemistryDetailComponent,
    JobRequestTestWbmSalinityDetailComponent,
    JobRequestTestStaticAgingDetailComponent,
    JobRequestTestHphtRheologyDetailComponent,
    JobRequestTestCustomDetailComponent,
    MatIconModule,
    MatButtonModule
  ],
  templateUrl: './job-request-overview.component.html',
  styleUrl: './job-request-overview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JobRequestOverviewComponent implements OnChanges, OnInit {
  @Input() overviewGroups: OverviewGroupDto[] = [];
  @Input() jobId: number | null = null;
  @Input() jobRequestId: number | null = null;
  @Input() designId: number | null = null;
  @Input() baseFluidSpecificGravity: number | null = null;
  @Input() bariteSpecificGravity: number | null = null;
  @Input() lowGravitySolidsSpecificGravity: number | null = null;
  @Input() isEditable = false;

  @Output() changed = new EventEmitter<void>();

  protected readonly waterDensityPpg = 8.345;
  protected readonly pycnometerRunIndices = [1, 2, 3, 4];
  protected readonly defaultRheologyTemperatures = [80, 120, 150];
  protected readonly defaultElectricalStabilityTemperatures = [120, 150];
  protected readonly defaultHphtFluidLossTemperature = 250;
  protected readonly defaultHphtFluidLossPressure = 500;
  protected readonly defaultPptTemperature = 250;
  protected readonly defaultPptDiscGrade = 50;
  protected readonly defaultPptDifferentialPressure = 2500;
  protected readonly defaultApiFluidLossFiltrateVolumeLimit = 10;
  protected readonly defaultRetortSampleVolume = 10;
  protected readonly defaultStaticAgingMudVolume = 350;
  private bariteDensity = 4.2;
  private readonly defaultPycnometerProfileName = 'mud lab';
  protected readonly rheologyRows: RheologyRow[] = [
    { key: 'rpm600', label: '600 RPM', type: 'number', min: 0, step: 1 },
    { key: 'rpm300', label: '300 RPM', type: 'number', min: 0, step: 1 },
    { key: 'rpm200', label: '200 RPM', type: 'number', min: 0, step: 1 },
    { key: 'rpm100', label: '100 RPM', type: 'number', min: 0, step: 1 },
    { key: 'rpm6', label: '6 RPM', type: 'number', min: 0, step: 1 },
    { key: 'rpm3', label: '3 RPM', type: 'number', min: 0, step: 1 },
    { key: 'gel10s', label: '10s', type: 'number', min: 0, step: 1 },
    { key: 'gel10m', label: '10m', type: 'number', min: 0, step: 1 },
    { key: 'gel30m', label: '30m', type: 'number', min: 0, step: 1 }
  ];
  protected readonly defaultHphtRheologyTemperatures = [80];
  protected readonly hphtRheologyRows: HphtRheologyRowView[] = [
    { key: 'rpm600', label: '600 RPM', type: 'number', min: 0, step: 1, tttm: true },
    { key: 'rpm300', label: '300 RPM', type: 'number', min: 0, step: 1, tttm: true },
    { key: 'rpm200', label: '200 RPM', type: 'number', min: 0, step: 1, tttm: true },
    { key: 'rpm100', label: '100 RPM', type: 'number', min: 0, step: 1, tttm: true },
    { key: 'rpm60', label: '60 RPM', type: 'number', min: 0, step: 1, tttm: true },
    { key: 'rpm30', label: '30 RPM', type: 'number', min: 0, step: 1, tttm: true },
    { key: 'rpm20', label: '20 RPM', type: 'number', min: 0, step: 1, tttm: true },
    { key: 'rpm10', label: '10 RPM', type: 'number', min: 0, step: 1, tttm: true },
    { key: 'rpm6', label: '6 RPM', type: 'number', min: 0, step: 1, tttm: true },
    { key: 'rpm3', label: '3 RPM', type: 'number', min: 0, step: 1, tttm: true },
    { key: 'gel10s600', label: '600 RPM (10 sec)', type: 'number', min: 0, step: 1 },
    { key: 'gel10s300', label: '300 RPM (10 sec)', type: 'number', min: 0, step: 1 },
    { key: 'gel10s', label: '10 second Gel', type: 'number', min: 0, step: 1 },
    { key: 'gel10m600', label: '600 RPM (10 min)', type: 'number', min: 0, step: 1 },
    { key: 'gel10m', label: '10 minute Gel', type: 'number', min: 0, step: 1 },
    { key: 'end600', label: '600 RPM (END)', type: 'number', min: 0, step: 1 }
  ];

  private expandedTests = new Set<string>();
  private testFieldValuesByRow = new Map<string, Map<string, string>>();
  private autoGeneratedWbmBariteRows = new Set<string>();
  private manuallyEditedWbmBariteRows = new Set<string>();
  private rheologyTemperaturesByRow = new Map<string, number[]>();
  private hphtRheologyTemperaturesByRow = new Map<string, HphtRheologyReadingView[]>();
  private electricalStabilityReadingsByRow = new Map<string, ElectricalStabilityReadingState[]>();
  private electricalStabilityStoredReadingCountByRow = new Map<string, number>();
  private startingTests = new Set<string>();
  private savingTests = new Set<string>();
  private completingTests = new Set<string>();
  private cancellingTests = new Set<string>();
  private renamingTests = new Set<string>();
  private testErrors = new Map<string, string>();
  private pycnometerProfilePickerOpenByTest = new Set<string>();
  protected addingTestGroup = false;
  protected renamingTestGroupId: number | null = null;
  protected deletingTestGroupId: number | null = null;
  protected testGroupActionError = '';
  protected renameModalOpen = false;
  protected renameModalGroupId: number | null = null;
  protected renameInputValue = '';
  protected deleteConfirmModalOpen = false;
  protected deleteConfirmGroupId: number | null = null;
  protected addingTest = false;
  protected addTestActionError = '';
  protected addTestModalOpen = false;
  protected addTestTargetGroupId: number | null = null;
  protected selectedAddTestTypeId: number | null = null;
  protected availableTestTypes: TestTypeLookupDto[] = [];
  protected loadingTestTypes = false;
  protected addTestReadings: AddTestReadingInput[] = [];
  protected loadingAddTestReadings = false;

  protected pycnometerProfiles: PycnometerProfileSummary[] = [];
  protected pycnometerProfilesLoading = false;
  protected pycnometerProfilesError = '';
  protected attachmentModalOpen = false;
  protected uploadingAttachment = false;
  protected attachmentActionError = '';
  protected selectedAttachmentFile: File | null = null;
  protected attachmentModalTestInputId: number | null = null;
  private downloadingAttachments = new Set<number>();

  private readonly testInputService = inject(TestInputService);
  private readonly testOutputService = inject(TestOutputService);
  private readonly pycnometerProfilesService = inject(PycnometerProfilesService);
  private readonly jobRequestsService = inject(JobRequestsService);
  private readonly designService = inject(DesignService);
  private readonly attachmentService = inject(AttachmentService);
  private readonly systemSettingsService = inject(SystemSettingsService);
  private readonly spreadsheetService = inject(SpreadsheetService);
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.loadPycnometerProfiles();
    this.loadBariteDensity();
  }

  private loadBariteDensity(): void {
    this.systemSettingsService.getFluidConstants('WBM').subscribe({
      next: constants => {
        if (Number.isFinite(constants.bariteSG) && constants.bariteSG > 0) {
          this.bariteDensity = constants.bariteSG;
        }
        this.refreshWbmTitrationBariteDefaults();
        this.cdr.markForCheck();
      },
      error: () => this.cdr.markForCheck()
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['overviewGroups']) return;

    const expandedByDefault = new Set<string>();
    const validKeys = new Set<string>();

    this.overviewGroups = this.overviewGroups.map(group => ({
      ...group,
      overviews: group.overviews.map(overview => ({
        ...overview,
        rows: overview.rows.filter(row => row.value !== 'Cancelled')
      }))
    }));

    this.overviewGroups.forEach((group, groupIndex) => {
      group.overviews.forEach((overview, overviewIndex) => {
        if (overview.type !== 'Tests') return;

        overview.rows.forEach((row, rowIndex) => {
          const key = this.testKey(groupIndex, overviewIndex, rowIndex);
          validKeys.add(key);
          this.prefillValuesFromRowParameters(key, row);
          if (this.isInProgressStatus(row.value)) {
            expandedByDefault.add(key);
          }
        });
      });
    });

    this.overviewGroups.forEach((group, groupIndex) => {
      group.overviews.forEach((overview, overviewIndex) => {
        if (overview.type !== 'Tests') return;

        overview.rows.forEach((row, rowIndex) => {
          const testKey = this.testKey(groupIndex, overviewIndex, rowIndex);
          this.applySalinityDefaults(testKey, row);
          this.applyWbmChemistryDefaults(testKey, row);
        });
      });
    });

    this.refreshWbmTitrationBariteDefaults();

    this.expandedTests.forEach(key => {
      if (!validKeys.has(key)) {
        this.expandedTests.delete(key);
        this.testFieldValuesByRow.delete(key);
        this.startingTests.delete(key);
        this.savingTests.delete(key);
        this.completingTests.delete(key);
        this.cancellingTests.delete(key);
        this.renamingTests.delete(key);
        this.testErrors.delete(key);
        this.pycnometerProfilePickerOpenByTest.delete(key);
        this.rheologyTemperaturesByRow.delete(key);
        this.electricalStabilityReadingsByRow.delete(key);
        this.electricalStabilityStoredReadingCountByRow.delete(key);
      }
    });

    this.expandedTests = new Set([...this.expandedTests, ...expandedByDefault]);
    //this.applyDefaultPycnometerProfile();
  }

  protected statusClass(value?: string | null): string {
    if (!value) return 'status-unknown';
    const s = value.toLowerCase();
    if (s.includes('pass') || s.includes('ok') || s.includes('complete')) return 'status-pass';
    if (s.includes('fail') || s.includes('error')) return 'status-fail';
    if (s.includes('pending') || s.includes('progress')) return 'status-pending';
    return 'status-unknown';
  }

  protected isTestsOverview(overview: OverviewDto): boolean {
    return overview.type === 'Tests';
  }

  protected canPrintWorksheet(overview: OverviewDto): boolean {
    return !!this.resolveWorksheetJobId() && !!overview.testGroupId;
  }

  protected printWorksheet(overview: OverviewDto): void {
    const worksheetJobId = this.resolveWorksheetJobId();
    if (!worksheetJobId || !overview.testGroupId) {
      this.testGroupActionError = 'Cannot print worksheet: missing job or test group identifier.';
      return;
    }

    window.open(`/jobs/${worksheetJobId}/worksheets/${overview.testGroupId}`, '_blank');
  }

  protected addTestGroup(): void {
    if ((!this.jobRequestId && !this.designId) || this.addingTestGroup) return;

    const enteredName = window.prompt('Enter a name for the new test group (leave blank for default):', '');
    if (enteredName === null) {
      return;
    }

    const groupName = enteredName.trim();

    this.addingTestGroup = true;
    this.testGroupActionError = '';

    const add$ = this.designId
      ? this.designService.addTestGroupToDesign(this.designId, groupName || undefined)
      : this.jobRequestsService.addTestGroup(this.jobRequestId!, groupName || undefined);

    add$.subscribe({
      next: () => {
        this.addingTestGroup = false;
        this.changed.emit();
        this.cdr.markForCheck();
      },
      error: () => {
        this.addingTestGroup = false;
        this.testGroupActionError = 'Failed to add test group.';
        this.cdr.markForCheck();
      }
    });
  }

  protected renameTestGroup(testGroupId: number | null | undefined, currentName?: string): void {
    if (!testGroupId || (!this.jobRequestId && !this.designId) || this.renamingTestGroupId !== null) return;

    this.renameModalGroupId = testGroupId;
    this.renameInputValue = currentName ?? '';
    this.renameModalOpen = true;
  }

  protected closeRenameModal(): void {
    this.renameModalOpen = false;
    this.renameModalGroupId = null;
    this.renameInputValue = '';
  }

  protected confirmRename(): void {
    const name = this.renameInputValue.trim();
    if (!name || !this.renameModalGroupId || (!this.jobRequestId && !this.designId)) return;

    const testGroupId = this.renameModalGroupId;
    this.renameModalOpen = false;
    this.renamingTestGroupId = testGroupId;
    this.testGroupActionError = '';

    const rename$ = this.designId
      ? this.designService.renameTestGroupInDesign(this.designId, testGroupId, name)
      : this.jobRequestsService.renameTestGroup(this.jobRequestId!, testGroupId, name);

    rename$.subscribe({
      next: () => {
        this.renamingTestGroupId = null;
        this.renameModalGroupId = null;
        this.changed.emit();
        this.cdr.markForCheck();
      },
      error: () => {
        this.renamingTestGroupId = null;
        this.testGroupActionError = 'Failed to rename test group.';
        this.cdr.markForCheck();
      }
    });
  }

  protected deleteTestGroup(testGroupId: number | null | undefined): void {
    if (!testGroupId || (!this.jobRequestId && !this.designId) || this.deletingTestGroupId !== null) return;

    this.deleteConfirmGroupId = testGroupId;
    this.deleteConfirmModalOpen = true;
  }

  protected closeDeleteConfirmModal(): void {
    if (this.deletingTestGroupId !== null) return;
    this.deleteConfirmModalOpen = false;
    this.deleteConfirmGroupId = null;
  }

  protected confirmDelete(): void {
    const testGroupId = this.deleteConfirmGroupId;
    if (!testGroupId || (!this.jobRequestId && !this.designId)) return;

    this.deleteConfirmModalOpen = false;
    this.deletingTestGroupId = testGroupId;
    this.testGroupActionError = '';

    const delete$ = this.designId
      ? this.designService.deleteTestGroupFromDesign(this.designId, testGroupId)
      : this.jobRequestsService.deleteTestGroup(this.jobRequestId!, testGroupId);

    delete$.subscribe({
      next: () => {
        this.deletingTestGroupId = null;
        this.deleteConfirmGroupId = null;
        this.changed.emit();
        this.cdr.markForCheck();
      },
      error: () => {
        this.deletingTestGroupId = null;
        this.testGroupActionError = 'Failed to delete test group.';
        this.cdr.markForCheck();
      }
    });
  }

  protected openAddTestModal(testGroupId: number | null | undefined): void {
    if ((!this.jobRequestId && !this.designId) || !testGroupId || this.addingTest) {
      return;
    }

    this.addTestTargetGroupId = testGroupId;
    this.selectedAddTestTypeId = null;
    this.addTestReadings = [];
    this.addTestActionError = '';
    this.addTestModalOpen = true;

    if (this.availableTestTypes.length > 0 || this.loadingTestTypes) {
      return;
    }

    this.loadingTestTypes = true;
    this.jobRequestsService.getMudCheckTestTypes().subscribe({
      next: testTypes => {
        this.availableTestTypes = testTypes;
        this.loadingTestTypes = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.addTestActionError = 'Failed to load test types.';
        this.loadingTestTypes = false;
        this.cdr.markForCheck();
      }
    });
  }

  protected closeAddTestModal(): void {
    if (this.addingTest) {
      return;
    }

    this.addTestModalOpen = false;
    this.addTestTargetGroupId = null;
    this.selectedAddTestTypeId = null;
    this.addTestReadings = [];
    this.loadingAddTestReadings = false;
    this.addTestActionError = '';
  }

  protected selectAddTestType(value: string): void {
    const parsed = Number(value);
    this.selectedAddTestTypeId = Number.isInteger(parsed) && parsed > 0 ? parsed : null;
    this.addTestReadings = [];
    this.loadingAddTestReadings = false;

    if (this.isAddTestHphtRheology()) {
      this.initAddTestReadings();
    }
  }

  protected isAddTestHphtRheology(): boolean {
    const selected = this.availableTestTypes.find(tt => tt.id === this.selectedAddTestTypeId);
    return !!selected && this.testAlias(`${selected.alias ?? ''} ${selected.name ?? ''}`) === 'hphtRheology';
  }

  protected addAddTestReading(): void {
    this.addTestReadings = [...this.addTestReadings, { temperature: '', pressure: '0' }];
  }

  protected removeAddTestReading(index: number): void {
    if (this.addTestReadings.length <= 1) return;
    this.addTestReadings = this.addTestReadings.filter((_, i) => i !== index);
  }

  protected updateAddTestReading(index: number, field: keyof AddTestReadingInput, value: string): void {
    this.addTestReadings = this.addTestReadings.map((reading, i) =>
      i === index ? { ...reading, [field]: value } : reading
    );
  }

  protected addTestReadingsError(): string {
    const parsed = this.addTestReadings.map(reading => this.parseAddTestReading(reading));
    if (parsed.length === 0 || parsed.some(reading => reading === null)) {
      return 'Each reading requires a temperature and a pressure.';
    }

    const temperatures = parsed.map(reading => reading!.temperature);
    return new Set(temperatures).size === temperatures.length ? '' : 'Reading temperatures must be unique.';
  }

  protected canConfirmAddTest(): boolean {
    return !!(this.jobRequestId || this.designId)
      && !!this.addTestTargetGroupId
      && !!this.selectedAddTestTypeId
      && !this.addingTest
      && (!this.isAddTestHphtRheology() || (!this.loadingAddTestReadings && !this.addTestReadingsError()));
  }

  protected confirmAddTest(): void {
    if (!this.jobRequestId || !this.addTestTargetGroupId || !this.selectedAddTestTypeId || this.addingTest) {
      return;
    }

    const readings = this.isAddTestHphtRheology()
      ? this.addTestReadings
          .map(reading => this.parseAddTestReading(reading))
          .filter((reading): reading is { temperature: number; pressure: number } => reading !== null)
      : undefined;

    this.addingTest = true;
    this.addTestActionError = '';

    this.jobRequestsService.addTestToGroup(this.jobRequestId, this.addTestTargetGroupId, this.selectedAddTestTypeId, readings).subscribe({
      next: () => {
        this.addingTest = false;
        this.addTestModalOpen = false;
        this.addTestTargetGroupId = null;
        this.selectedAddTestTypeId = null;
        this.addTestReadings = [];
        this.changed.emit();
        this.cdr.markForCheck();
      },
      error: () => {
        this.addTestActionError = 'Failed to add test to this group.';
        this.addingTest = false;
        this.cdr.markForCheck();
      }
    });
  }

  // Seeds readings from the job request's downhole HPHT profile, or a single blank reading.
  private initAddTestReadings(): void {
    const blank: AddTestReadingInput[] = [{ temperature: '', pressure: '0' }];
    if (!this.jobRequestId) {
      this.addTestReadings = blank;
      return;
    }

    this.loadingAddTestReadings = true;
    this.jobRequestsService.getForEdit(this.jobRequestId).subscribe({
      next: dto => {
        const profile = (dto.hphtItems ?? [])
          .filter(item => item.temperature !== null && item.pressure !== null)
          .map(item => ({ temperature: String(item.temperature), pressure: String(item.pressure) }));
        this.finishInitAddTestReadings(profile.length > 0 ? profile : blank);
      },
      error: () => this.finishInitAddTestReadings(blank)
    });
  }

  private finishInitAddTestReadings(readings: AddTestReadingInput[]): void {
    if (!this.loadingAddTestReadings || !this.isAddTestHphtRheology()) return;
    this.addTestReadings = readings;
    this.loadingAddTestReadings = false;
    this.cdr.markForCheck();
  }

  private parseAddTestReading(reading: AddTestReadingInput): { temperature: number; pressure: number } | null {
    if (reading.temperature.trim() === '' || reading.pressure.trim() === '') return null;
    const temperature = Number(reading.temperature);
    const pressure = Number(reading.pressure);
    if (!Number.isInteger(temperature) || !Number.isInteger(pressure) || temperature <= 0 || pressure < 0) return null;
    return { temperature, pressure };
  }

  protected isExpanded(groupIndex: number, overviewIndex: number, rowIndex: number): boolean {
    return this.expandedTests.has(this.testKey(groupIndex, overviewIndex, rowIndex));
  }

  protected toggleTest(groupIndex: number, overviewIndex: number, rowIndex: number): void {
    const key = this.testKey(groupIndex, overviewIndex, rowIndex);
    if (this.expandedTests.has(key)) {
      this.expandedTests.delete(key);
      return;
    }

    this.expandedTests.add(key);
  }

  protected focusNextTestInput(event: KeyboardEvent): void {
    const fieldset = event.currentTarget as HTMLFieldSetElement;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const editableInputs = Array.from(fieldset.querySelectorAll<HTMLElement>(
      'input:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([readonly]):not([disabled]):not([tabindex="-1"])'
    ));
    const currentIndex = editableInputs.indexOf(target);
    if (currentIndex < 0 || currentIndex >= editableInputs.length - 1) return;

    event.preventDefault();
    editableInputs[currentIndex + 1].focus();
  }

  protected showStartButton(status?: string | null): boolean {
    if (!status) return false;
    const normalized = status.toLowerCase();
    return normalized.includes('not started') || normalized.includes('notstarted');
  }

  protected isTestDataEntryLocked(status?: string | null): boolean {
    return this.showStartButton(status);
  }

  protected startTest(
    row: OverviewRowDto,
    groupIndex: number,
    overviewIndex: number,
    rowIndex: number,
    event: Event
  ): void {
    event.stopPropagation();

    const key = this.testKey(groupIndex, overviewIndex, rowIndex);
    this.testErrors.delete(key);

    if (!row.testInputId) {
      row.value = 'In Progress';
      this.expandedTests.add(key);
      return;
    }

    this.startingTests.add(key);
    this.testInputService.start(row.testInputId).subscribe({
      next: () => {
        row.value = 'In Progress';
        this.expandedTests.add(key);
        this.startingTests.delete(key);
        this.cdr.markForCheck();
      },
      error: (err: { error?: { title?: string } }) => {
        this.testErrors.set(key, err?.error?.title ?? 'Failed to start test.');
        this.startingTests.delete(key);
        this.cdr.markForCheck();
      }
    });
  }

  protected testDetailSections(testLabel: string): TestDetailSection[] {
    const alias = this.testAlias(testLabel);
    return alias ? (TEST_DETAIL_SCHEMAS[alias] ?? []) : [];
  }

  protected hasTestDetailTemplate(testLabel: string): boolean {
    return this.testDetailSections(testLabel).length > 0;
  }

  protected isRheologyTest(testLabel: string): boolean {
    const alias = this.testAlias(testLabel);
    return alias === 'rheology';
  }

  protected rheologyTemperatures(testKey: string): number[] {
    return this.rheologyTemperaturesByRow.get(testKey) ?? this.defaultRheologyTemperatures;
  }

  protected rheologyGridColumns(testKey: string): string {
    const count = this.rheologyTemperatures(testKey).length;
    return `140px repeat(${Math.max(count, 1)}, 220px)`;
  }

  protected rheologyPlasticViscosity(testKey: string, temperature: number): string {
    const rpm600 = this.numericValue(testKey, this.rheologyFieldKey(temperature, 'rpm600'));
    const rpm300 = this.numericValue(testKey, this.rheologyFieldKey(temperature, 'rpm300'));
    if (rpm600 === null || rpm300 === null) return '';
    return String(rpm600 - rpm300);
  }

  protected rheologyYieldPoint(testKey: string, temperature: number): string {
    const rpm300 = this.numericValue(testKey, this.rheologyFieldKey(temperature, 'rpm300'));
    const pv = Number(this.rheologyPlasticViscosity(testKey, temperature));
    if (rpm300 === null || !Number.isFinite(pv)) return '';
    return String(rpm300 - pv);
  }

  protected isDensityTest(testLabel: string): boolean {
    const alias = this.testAlias(testLabel);
    return alias === 'density';
  }

  protected isPycnometerTest(testLabel: string): boolean {
    const alias = this.testAlias(testLabel);
    return alias === 'pycnometer';
  }

  protected rheologyFieldKey(temperature: number, rowKey: string): string {
    return `t${temperature}_${rowKey}`;
  }

  protected addRheologyTemperature(testKey: string): void {
    const temperatures = this.ensureRheologyTemperatures(testKey);
    const max = temperatures.length > 0 ? Math.max(...temperatures) : 70;

    let candidate = max + 10;
    while (temperatures.includes(candidate)) {
      candidate += 10;
    }

    this.rheologyTemperaturesByRow.set(testKey, [...temperatures, candidate].sort((a, b) => a - b));
  }

  protected removeRheologyTemperature(testKey: string, temperature: number): void {
    const temperatures = this.ensureRheologyTemperatures(testKey);
    if (!temperatures.includes(temperature)) {
      return;
    }

    const remaining = temperatures.filter(t => t !== temperature);
    this.rheologyTemperaturesByRow.set(testKey, remaining);

    const values = this.valueMap(testKey);
    for (const keySuffix of this.rheologyTemperatureKeySuffixes()) {
      values.delete(this.rheologyFieldKey(temperature, keySuffix));
    }
  }

  protected updateRheologyTemperature(testKey: string, oldTemperature: number, nextValue: string): void {
    const parsed = Number(nextValue);
    if (!Number.isFinite(parsed)) {
      return;
    }

    const newTemperature = Math.round(parsed);
    if (newTemperature <= 0) {
      return;
    }

    const temperatures = this.ensureRheologyTemperatures(testKey);
    const index = temperatures.findIndex(t => t === oldTemperature);
    if (index === -1 || oldTemperature === newTemperature) {
      return;
    }

    if (temperatures.includes(newTemperature)) {
      this.testErrors.set(testKey, `Temperature ${newTemperature} already exists.`);
      this.cdr.markForCheck();
      return;
    }

    const updatedTemperatures = [...temperatures];
    updatedTemperatures[index] = newTemperature;
    this.rheologyTemperaturesByRow.set(testKey, updatedTemperatures.sort((a, b) => a - b));

    const values = this.valueMap(testKey);
    for (const keySuffix of this.rheologyTemperatureKeySuffixes()) {
      const oldKey = this.rheologyFieldKey(oldTemperature, keySuffix);
      if (!values.has(oldKey)) {
        continue;
      }

      const value = values.get(oldKey) ?? '';
      values.delete(oldKey);

      const newKey = this.rheologyFieldKey(newTemperature, keySuffix);
      if (!values.has(newKey)) {
        values.set(newKey, value);
      }
    }

    this.testErrors.delete(testKey);
  }

  protected hphtRheologyTemperatures(testKey: string): HphtRheologyReadingView[] {
    return this.hphtRheologyTemperaturesByRow.get(testKey)
      ?? this.defaultHphtRheologyTemperatures.map((temperature, index) => ({ id: index + 1, temperature }));
  }

  protected hphtRheologyGridColumns(testKey: string): string {
    const count = this.hphtRheologyTemperatures(testKey).length;
    const fann35Column = this.hphtFann35Reference(testKey) ? 1 : 0;
    return `140px repeat(${fann35Column + Math.max(count, 1)}, 220px)`;
  }

  protected hphtRheologyFieldKey(readingId: number, rowKey: string): string {
    return `reading:${readingId}:${rowKey}`;
  }

  protected hphtCalculated600Rpm(testKey: string, readingId: number): string {
    const rpm300 = this.numericValue(testKey, this.hphtRheologyFieldKey(readingId, 'rpm300'));
    const rpm200 = this.numericValue(testKey, this.hphtRheologyFieldKey(readingId, 'rpm200'));
    if (rpm300 === null || rpm200 === null) return '';
    return String((rpm300 - rpm200) * 3 + rpm300);
  }

  protected hphtPlasticViscosity(testKey: string, readingId: number): string {
    const rpm600 = this.numericValue(testKey, this.hphtRheologyFieldKey(readingId, 'rpm600'));
    const rpm300 = this.numericValue(testKey, this.hphtRheologyFieldKey(readingId, 'rpm300'));
    if (rpm600 === null || rpm300 === null) return '';
    return String(rpm600 - rpm300);
  }

  protected hphtYieldPoint(testKey: string, readingId: number): string {
    const rpm300 = this.numericValue(testKey, this.hphtRheologyFieldKey(readingId, 'rpm300'));
    const pv = Number(this.hphtPlasticViscosity(testKey, readingId));
    if (rpm300 === null || !Number.isFinite(pv)) return '';
    return String(rpm300 - pv);
  }

  protected addHphtRheologyReading(testKey: string): void {
    const readings = this.ensureHphtRheologyTemperatures(testKey);
    const max = readings.length > 0 ? Math.max(...readings.map(reading => reading.temperature)) : 70;

    let candidate = max + 10;
    while (readings.some(reading => reading.temperature === candidate)) {
      candidate += 10;
    }

    const id = Math.max(0, ...readings.map(reading => reading.id)) + 1;
    this.hphtRheologyTemperaturesByRow.set(testKey, [...readings, { id, temperature: candidate }]);
  }

  protected removeHphtRheologyReading(testKey: string, readingId: number): void {
    const readings = this.ensureHphtRheologyTemperatures(testKey);
    if (!readings.some(reading => reading.id === readingId)) {
      return;
    }

    const remaining = readings.filter(reading => reading.id !== readingId);
    this.hphtRheologyTemperaturesByRow.set(testKey, remaining);

    const values = this.valueMap(testKey);
    for (const keySuffix of this.hphtRheologyTemperatureKeySuffixes()) {
      values.delete(this.hphtRheologyFieldKey(readingId, keySuffix));
    }
  }

  protected updateHphtRheologyTemperature(testKey: string, readingId: number, nextValue: string): void {
    const parsed = Number(nextValue);
    if (!Number.isFinite(parsed)) {
      return;
    }

    const newTemperature = Math.round(parsed);
    if (newTemperature <= 0) {
      return;
    }

    const readings = this.ensureHphtRheologyTemperatures(testKey);
    const index = readings.findIndex(reading => reading.id === readingId);
    if (index === -1 || readings[index].temperature === newTemperature) {
      return;
    }

    const updatedReadings = [...readings];
    updatedReadings[index] = { ...updatedReadings[index], temperature: newTemperature };
    this.hphtRheologyTemperaturesByRow.set(testKey, updatedReadings);

    const values = this.valueMap(testKey);
    values.set(this.hphtRheologyFieldKey(readingId, 'temperature'), String(newTemperature));

    this.testErrors.delete(testKey);
  }

  protected hphtFann35Reference(testKey: string): Fann35ReferenceView | null {
    const sourceTestKey = this.findSiblingRheologyTestKey();
    if (!sourceTestKey) return null;

    const values = this.valueMap(sourceTestKey);
    const sourceTemperatures = this.rheologyTemperatures(sourceTestKey);
    const temperature = sourceTemperatures.find(candidate =>
      this.rheologyRows.some(row => (values.get(this.rheologyFieldKey(candidate, row.key)) ?? '').trim() !== '')
    ) ?? sourceTemperatures[0];
    if (temperature === undefined) return null;

    const directRowKeys = new Set(['rpm600', 'rpm300', 'rpm200', 'rpm100', 'rpm6', 'rpm3', 'gel10s', 'gel10m']);
    const rowValues: Record<string, string> = {};
    for (const row of this.hphtRheologyRows) {
      rowValues[row.key] = directRowKeys.has(row.key)
        ? values.get(this.rheologyFieldKey(temperature, row.key)) ?? ''
        : '---';
    }

    return {
      requestedTemperature: String(temperature),
      requestedPressure: values.get(this.rheologyFieldKey(temperature, 'pressure')) ?? '',
      calculated600Rpm: this.rheologyCalculated600Rpm(sourceTestKey, temperature),
      rowValues,
      plasticViscosity: this.rheologyPlasticViscosity(sourceTestKey, temperature),
      yieldPoint: this.rheologyYieldPoint(sourceTestKey, temperature)
    };
  }

  private findSiblingRheologyTestKey(): string | null {
    for (let groupIndex = 0; groupIndex < this.overviewGroups.length; groupIndex++) {
      const group = this.overviewGroups[groupIndex];

      for (let overviewIndex = 0; overviewIndex < group.overviews.length; overviewIndex++) {
        const overview = group.overviews[overviewIndex];
        if (overview.type !== 'Tests') continue;

        for (let rowIndex = 0; rowIndex < overview.rows.length; rowIndex++) {
          const row = overview.rows[rowIndex];
          if (this.resolveTestAlias(row) === 'rheology') {
            return this.testKey(groupIndex, overviewIndex, rowIndex);
          }
        }
      }
    }

    return null;
  }

  private rheologyCalculated600Rpm(testKey: string, temperature: number): string {
    const rpm300 = this.numericValue(testKey, this.rheologyFieldKey(temperature, 'rpm300'));
    const rpm200 = this.numericValue(testKey, this.rheologyFieldKey(temperature, 'rpm200'));
    if (rpm300 === null || rpm200 === null) return '';
    return String((rpm300 - rpm200) * 3 + rpm300);
  }

  protected electricalStabilityReadingIndices(testKey: string): number[] {
    return this.ensureElectricalStabilityReadings(testKey).map(reading => reading.index);
  }

  protected electricalStabilityShowAllStoredReadings(testKey: string): boolean {
    return (this.electricalStabilityStoredReadingCountByRow.get(testKey) ?? 0) > 1;
  }

  protected addElectricalStabilityReading(testKey: string): void {
    const readings = this.ensureElectricalStabilityReadings(testKey);
    const nextIndex = readings.length > 0 ? Math.max(...readings.map(reading => reading.index)) + 1 : 1;
    const maxTemperature = readings.length > 0
      ? Math.max(...readings.map(reading => reading.temperature))
      : this.defaultElectricalStabilityTemperatures[0];
    const nextTemperature = maxTemperature + 30;

    const values = this.valueMap(testKey);
    values.set(this.electricalStabilityFieldKey(nextIndex, 'temperature'), String(nextTemperature));
    values.set(this.electricalStabilityFieldKey(nextIndex, 'id'), this.createElectricalStabilityReadingId());

    this.electricalStabilityReadingsByRow.set(testKey, [...readings, { index: nextIndex, temperature: nextTemperature }]);
  }

  protected removeElectricalStabilityReading(testKey: string, readingIndex: number): void {
    const readings = this.ensureElectricalStabilityReadings(testKey);
    if (readings.length <= 1) {
      return;
    }

    const remaining = readings.filter(reading => reading.index !== readingIndex);
    this.electricalStabilityReadingsByRow.set(testKey, remaining);

    const values = this.valueMap(testKey);
    for (const keyPart of ['id', 'temperature', 'voltage1', 'voltage2', 'voltage3']) {
      values.delete(this.electricalStabilityFieldKey(readingIndex, keyPart));
    }
  }

  protected fieldValue(testKey: string, field: TestDetailField): string {
    if (field.type === 'computed') {
      return this.computedFieldValue(testKey, field.key);
    }

    return this.valueMap(testKey).get(field.key) ?? '';
  }

  protected updateFieldValue(testKey: string, fieldKey: string, value: string): void {
    this.valueMap(testKey).set(fieldKey, value);
  }

  protected onTestFieldChanged(testKey: string, event: { fieldKey: string; value: string }): void {
    if (this.isTestDataEntryLocked(this.testStatusByKey(testKey))) {
      return;
    }

    this.updateFieldValue(testKey, event.fieldKey, event.value);

    const row = this.rowForTestKey(testKey);
    const alias = row ? this.resolveTestAlias(row) : null;
    if (alias === 'wbmTitration') {
      if (event.fieldKey === 'bariteLbPerBbl') {
        if (event.value.trim()) {
          this.manuallyEditedWbmBariteRows.add(testKey);
          this.autoGeneratedWbmBariteRows.delete(testKey);
        } else {
          this.manuallyEditedWbmBariteRows.delete(testKey);
          this.autoGeneratedWbmBariteRows.delete(testKey);
        }
      }

      this.applyWbmTitrationBariteDefault(testKey, row);
    } else {
      this.refreshWbmTitrationBariteDefaults();
    }

    const match = /^esReading(\d+)Temperature$/i.exec(event.fieldKey);
    if (!match) {
      return;
    }

    const index = Number(match[1]);
    const parsed = Number(event.value);
    if (!Number.isFinite(parsed)) {
      return;
    }

    const readings = this.ensureElectricalStabilityReadings(testKey).map(reading =>
      reading.index === index
        ? { ...reading, temperature: Math.round(parsed) }
        : reading
    );

    this.electricalStabilityReadingsByRow.set(testKey, readings);
  }

  protected fieldValueByKey(testKey: string, fieldKey: string): string {
    return this.valueMap(testKey).get(fieldKey) ?? '';
  }

  protected computedFieldValueByKey(testKey: string, fieldKey: string): string {
    return this.computedFieldValue(testKey, fieldKey);
  }

  protected staticAgingMudCheckError(testKey: string): string {
    const duplicateNames = this.mudCheckMudWeightSpecificGravityResult(testKey).duplicateNames;
    if (duplicateNames.length === 0) {
      return '';
    }

    const names = duplicateNames.map(name => `"${name}"`).join(' and ');
    return `Multiple tests with the same name exist: ${names}. Rename the additional tests to Top, Middle, Bottom, or Combined Density.`;
  }

  protected densitySpecificGravity(testKey: string): string {
    return this.fieldValueByKey(testKey, 'specificGravity');
  }

  protected densityPpg(testKey: string): string {
    return this.computedFieldValue(testKey, 'densityPpg');
  }

  protected inputType(field: TestDetailField): 'number' | 'text' {
    return field.type === 'number' ? 'number' : 'text';
  }

  protected isComputedField(field: TestDetailField): boolean {
    return field.type === 'computed';
  }

  protected rowTestKey(groupIndex: number, overviewIndex: number, rowIndex: number): string {
    return this.testKey(groupIndex, overviewIndex, rowIndex);
  }

  protected resolveRowAlias(row: OverviewRowDto): string | null {
    return this.resolveTestAlias(row);
  }

  protected showSubmitButton(status?: string | null): boolean {
    if (!status) return false;
    const normalized = status.toLowerCase();
    return normalized.includes('progress') || normalized.includes('in progress');
  }

  protected saveParameters(
    row: OverviewRowDto,
    groupIndex: number,
    overviewIndex: number,
    rowIndex: number
  ): void {
    const key = this.testKey(groupIndex, overviewIndex, rowIndex);
    this.testErrors.delete(key);

    if (this.isTestDataEntryLocked(row.value)) {
      this.testErrors.set(key, 'Start the test before saving parameters.');
      return;
    }

    if (!row.testInputId) {
      this.testErrors.set(key, 'Cannot submit parameters: missing test identifier.');
      return;
    }

    const payload = this.collectSavePayload(key, row);
    const inputDto: UpdateTestInputDto = {
      parameters: payload.inputParameters
    };

    this.savingTests.add(key);
    this.testInputService.updateParameters(row.testInputId, inputDto).subscribe({
      next: () => {
        if (!payload.outputParameters) {
          this.savingTests.delete(key);
          this.cdr.markForCheck();
          return;
        }

        const outputDto: UpdateTestOutputDto = {
          parameters: payload.outputParameters,
          equipmentName: payload.outputParameters['equipmentName'] ?? null
        };

        this.testOutputService.updateParameters(row.testInputId!, outputDto).subscribe({
          next: () => {
            this.savingTests.delete(key);
            this.cdr.markForCheck();
          },
          error: (err: { error?: { title?: string } }) => {
            this.testErrors.set(key, err?.error?.title ?? 'Failed to submit output parameters.');
            this.savingTests.delete(key);
            this.cdr.markForCheck();
          }
        });
      },
      error: (err: { error?: { title?: string } }) => {
        this.testErrors.set(key, err?.error?.title ?? 'Failed to submit parameters.');
        this.savingTests.delete(key);
        this.cdr.markForCheck();
      }
    });
  }

  protected completeTest(
    row: OverviewRowDto,
    groupIndex: number,
    overviewIndex: number,
    rowIndex: number
  ): void {
    const key = this.testKey(groupIndex, overviewIndex, rowIndex);
    this.testErrors.delete(key);

    if (this.isTestDataEntryLocked(row.value)) {
      this.testErrors.set(key, 'Start the test before completing it.');
      return;
    }

    if (!row.testInputId) {
      this.testErrors.set(key, 'Cannot complete test: missing test identifier.');
      return;
    }

    const payload = this.collectSavePayload(key, row);
    const inputDto: UpdateTestInputDto = {
      parameters: payload.inputParameters
    };

    this.completingTests.add(key);
    this.testInputService.complete(row.testInputId, inputDto).subscribe({
      next: () => {
        if (!payload.outputParameters) {
          row.value = 'Passed';
          this.expandedTests.delete(key);
          this.completingTests.delete(key);
          this.cdr.markForCheck();
          this.changed.emit();
          return;
        }

        const outputDto: UpdateTestOutputDto = {
          parameters: payload.outputParameters,
          equipmentName: payload.outputParameters['equipmentName'] ?? null
        };

        this.testOutputService.updateParameters(row.testInputId!, outputDto).subscribe({
          next: () => {
            row.value = 'Passed';
            this.expandedTests.delete(key);
            this.completingTests.delete(key);
            this.cdr.markForCheck();
            this.changed.emit();
          },
          error: (err: { error?: { title?: string } }) => {
            this.testErrors.set(key, err?.error?.title ?? 'Failed to submit output parameters.');
            this.completingTests.delete(key);
            this.cdr.markForCheck();
          }
        });
      },
      error: (err: { error?: { title?: string } }) => {
        this.testErrors.set(key, err?.error?.title ?? 'Failed to complete test.');
        this.completingTests.delete(key);
        this.cdr.markForCheck();
      }
    });
  }

  protected cancelTest(
    row: OverviewRowDto,
    groupIndex: number,
    overviewIndex: number,
    rowIndex: number
  ): void {
    const key = this.testKey(groupIndex, overviewIndex, rowIndex);
    this.testErrors.delete(key);

    if (!row.testInputId) {
      this.testErrors.set(key, 'Cannot cancel test: missing test identifier.');
      return;
    }

    this.cancellingTests.add(key);
    this.testInputService.cancel(row.testInputId).subscribe({
      next: () => {
        this.overviewGroups = this.overviewGroups.map((group, currentGroupIndex) => {
          if (currentGroupIndex !== groupIndex) return group;

          return {
            ...group,
            overviews: group.overviews.map((overview, currentOverviewIndex) => {
              if (currentOverviewIndex !== overviewIndex) return overview;

              return {
                ...overview,
                rows: overview.rows.filter(overviewRow => overviewRow !== row)
              };
            })
          };
        });
        this.expandedTests.delete(key);
        this.testFieldValuesByRow.delete(key);
        this.startingTests.delete(key);
        this.savingTests.delete(key);
        this.completingTests.delete(key);
        this.cancellingTests.delete(key);
        this.renamingTests.delete(key);
        this.testErrors.delete(key);
        this.pycnometerProfilePickerOpenByTest.delete(key);
        this.rheologyTemperaturesByRow.delete(key);
        this.electricalStabilityReadingsByRow.delete(key);
        this.electricalStabilityStoredReadingCountByRow.delete(key);
        this.cdr.markForCheck();
      },
      error: (err: { error?: { title?: string } }) => {
        this.testErrors.set(key, err?.error?.title ?? 'Failed to cancel test.');
        this.cancellingTests.delete(key);
        this.cdr.markForCheck();
      }
    });
  }

  protected renameTest(
    row: OverviewRowDto,
    groupIndex: number,
    overviewIndex: number,
    rowIndex: number,
    event: Event
  ): void {
    event.stopPropagation();

    const key = this.testKey(groupIndex, overviewIndex, rowIndex);
    this.testErrors.delete(key);

    if (!row.testInputId) {
      this.testErrors.set(key, 'Cannot rename test: missing test identifier.');
      return;
    }

    const currentName = (row.label ?? '').trim();
    const enteredName = window.prompt('Enter a test name:', currentName);
    if (enteredName === null) {
      return;
    }

    const newName = enteredName.trim();
    if (!newName) {
      this.testErrors.set(key, 'Test name is required.');
      return;
    }

    if (newName === currentName) {
      return;
    }

    this.renamingTests.add(key);
    this.testInputService.rename(row.testInputId, { name: newName }).subscribe({
      next: () => {
        row.label = newName;
        this.renamingTests.delete(key);
        this.cdr.markForCheck();
      },
      error: (err: { error?: { title?: string } }) => {
        this.testErrors.set(key, err?.error?.title ?? 'Failed to rename test.');
        this.renamingTests.delete(key);
        this.cdr.markForCheck();
      }
    });
  }

  protected submitParameters(
    row: OverviewRowDto,
    groupIndex: number,
    overviewIndex: number,
    rowIndex: number
  ): void {
    this.saveParameters(row, groupIndex, overviewIndex, rowIndex);
  }

  protected isStarting(groupIndex: number, overviewIndex: number, rowIndex: number): boolean {
    return this.startingTests.has(this.testKey(groupIndex, overviewIndex, rowIndex));
  }

  protected isSubmitting(groupIndex: number, overviewIndex: number, rowIndex: number): boolean {
    return this.savingTests.has(this.testKey(groupIndex, overviewIndex, rowIndex));
  }

  protected isCompleting(groupIndex: number, overviewIndex: number, rowIndex: number): boolean {
    return this.completingTests.has(this.testKey(groupIndex, overviewIndex, rowIndex));
  }

  protected isCancelling(groupIndex: number, overviewIndex: number, rowIndex: number): boolean {
    return this.cancellingTests.has(this.testKey(groupIndex, overviewIndex, rowIndex));
  }

  protected isRenaming(groupIndex: number, overviewIndex: number, rowIndex: number): boolean {
    return this.renamingTests.has(this.testKey(groupIndex, overviewIndex, rowIndex));
  }

  protected isAnyTestActionRunning(groupIndex: number, overviewIndex: number, rowIndex: number): boolean {
    const key = this.testKey(groupIndex, overviewIndex, rowIndex);
    return this.startingTests.has(key)
      || this.savingTests.has(key)
      || this.completingTests.has(key)
        || this.cancellingTests.has(key)
        || this.renamingTests.has(key);
  }

  protected testError(groupIndex: number, overviewIndex: number, rowIndex: number): string | undefined {
    return this.testErrors.get(this.testKey(groupIndex, overviewIndex, rowIndex));
  }

  protected isPycnometerProfilePickerOpen(testKey: string): boolean {
    return this.pycnometerProfilePickerOpenByTest.has(testKey);
  }

  protected togglePycnometerProfilePicker(testKey: string): void {
    if (this.pycnometerProfilePickerOpenByTest.has(testKey)) {
      this.pycnometerProfilePickerOpenByTest.delete(testKey);
      return;
    }

    this.pycnometerProfilePickerOpenByTest.add(testKey);
  }

  protected usePycnometerProfile(testKey: string, profileId: number): void {
    this.pycnometerProfilesService.getById(profileId).subscribe({
      next: profile => {
        const values = this.valueMap(testKey);
        values.set('profileId', String(profile.id));
        values.set('profileName', profile.name ?? '');
        values.set('referenceVolume', String(profile.referenceVolume));
        values.set('cellVolume', String(profile.cellVolume));
        this.pycnometerProfilePickerOpenByTest.delete(testKey);
        this.cdr.markForCheck();
      },
      error: () => {
        this.testErrors.set(testKey, 'Failed to load selected pycnometer profile.');
        this.cdr.markForCheck();
      }
    });
  }

  protected pycnometerRunVp(testKey: string, run: number): string {
    const cellVolume = this.numericValue(testKey, 'cellVolume');
    const referenceVolume = this.numericValue(testKey, 'referenceVolume');
    const p1 = this.numericValue(testKey, this.pycnometerRunKey(run, 'p1'));
    const p2 = this.numericValue(testKey, this.pycnometerRunKey(run, 'p2'));

    if (cellVolume === null || referenceVolume === null || p1 === null || p2 === null || p2 === 0) {
      return '';
    }

    const volumeOfPowder = cellVolume - referenceVolume * (p1 / p2 - 1);
    return Number.isFinite(volumeOfPowder) ? volumeOfPowder.toFixed(3) : '';
  }

  protected pycnometerRunSg(testKey: string, run: number): string {
    const sampleWeight = this.numericValue(testKey, 'sampleWeight');
    const vp = Number(this.pycnometerRunVp(testKey, run));

    if (sampleWeight === null || !Number.isFinite(vp) || vp <= 0) {
      return '';
    }

    const sg = sampleWeight / vp;
    return Number.isFinite(sg) ? sg.toFixed(3) : '';
  }

  protected pycnometerAverageSg(testKey: string): string {
    const readings = this.pycnometerRunIndices
      .map(run => Number(this.pycnometerRunSg(testKey, run)))
      .filter(value => Number.isFinite(value) && value > 0);

    if (readings.length === 0) return '';
    const avg = readings.reduce((sum, value) => sum + value, 0) / readings.length;
    return avg.toFixed(3);
  }

  protected pycnometerAverageDensity(testKey: string): string {
    const avgSg = Number(this.pycnometerAverageSg(testKey));
    if (!Number.isFinite(avgSg) || avgSg <= 0) return '';
    return (avgSg * this.waterDensityPpg).toFixed(3);
  }

  protected pycnometerAverageAbsoluteVolume(testKey: string): string {
    const density = Number(this.pycnometerAverageDensity(testKey));
    if (!Number.isFinite(density) || density <= 0) return '';
    return (1 / density).toFixed(3);
  }

  private isInProgressStatus(status?: string | null): boolean {
    if (!status) return false;
    const normalized = status.toLowerCase();
    if (normalized.includes('not started') || normalized.includes('notstarted')) return false;

    return normalized.includes('progress')
      || normalized.includes('pending')
      || normalized.includes('started')
      || normalized.includes('ready for review');
  }

  private testStatusByKey(testKey: string): string | null | undefined {
    const [groupPart, overviewPart, rowPart] = testKey.split('-');
    const groupIndex = Number(groupPart);
    const overviewIndex = Number(overviewPart);
    const rowIndex = Number(rowPart);

    if (!Number.isInteger(groupIndex) || !Number.isInteger(overviewIndex) || !Number.isInteger(rowIndex)) {
      return undefined;
    }

    return this.overviewGroups[groupIndex]?.overviews[overviewIndex]?.rows[rowIndex]?.value;
  }

  private testKey(groupIndex: number, overviewIndex: number, rowIndex: number): string {
    return `${groupIndex}-${overviewIndex}-${rowIndex}`;
  }

  private rowForTestKey(testKey: string): OverviewRowDto | null {
    const indices = testKey.split('-').map(Number);
    if (indices.length !== 3 || indices.some(index => !Number.isInteger(index))) {
      return null;
    }

    const [groupIndex, overviewIndex, rowIndex] = indices;
    return this.overviewGroups[groupIndex]?.overviews[overviewIndex]?.rows[rowIndex] ?? null;
  }

  private resolveWorksheetJobId(): number | null {
    if (this.jobId) {
      return this.jobId;
    }

    const routeWithJob = [...this.route.pathFromRoot]
      .reverse()
      .find(route => route.snapshot.paramMap.has('jobId'));

    const rawJobId = routeWithJob?.snapshot.paramMap.get('jobId');
    const parsedJobId = rawJobId ? Number(rawJobId) : Number.NaN;

    return Number.isInteger(parsedJobId) && parsedJobId > 0 ? parsedJobId : null;
  }

  private testAlias(testLabel: string): string | null {
    const normalized = testLabel.toLowerCase();
    const compact = normalized.replace(/[^a-z0-9]/g, '');

    if (compact.includes('density')) return 'density';
    if (compact.includes('pycnometer')) return 'pycnometer';
    if (compact.includes('hphtrheology') || compact.includes('fann77')) return 'hphtRheology';
    if (compact.includes('rheology')) return 'rheology';
    if (compact.includes('electricalstability')) return 'electricalStability';
    if (compact.includes('hphtfluidloss') || compact.includes('hthp')) return 'hphtFluidLoss';
    if (compact.includes('apifluidloss')) return 'apiFluidLoss';
    if (compact.includes('permeabilityplugging') || compact.includes('ppt')) return 'ppt';
    if (compact.includes('retort')) return 'retort';
    if (compact.includes('particlesize') || compact.includes('psd')) return 'psd';
    if (compact.includes('s3s4') || compact.includes('wbmtitration') || compact.includes('wbmchlorides')) return 'wbmTitration';
    if (compact.includes('wbmsalinity') || compact.includes('wbmchemistry')) return 'wbmSalinity';
    if (compact.includes('salinity') || compact.includes('nafchemistry')) return 'salinity';
    if (compact.includes('staticaging')) return 'staticAging';

    return null;
  }

  private valueMap(testKey: string): Map<string, string> {
    let values = this.testFieldValuesByRow.get(testKey);
    if (!values) {
      values = new Map<string, string>();
      this.testFieldValuesByRow.set(testKey, values);
    }

    return values;
  }

  private prefillValuesFromRowParameters(testKey: string, row: OverviewRowDto): void {
    const parameters: Record<string, string | null> = {};
    for (const source of [row.inputParameters ?? {}, row.outputParameters ?? {}, row.parameters ?? {}]) {
      for (const [fieldKey, fieldValue] of Object.entries(source)) {
        const current = parameters[fieldKey];
        if (current === undefined || current === null || current === '') {
          parameters[fieldKey] = fieldValue;
          continue;
        }

        if (fieldValue !== null && fieldValue !== undefined && fieldValue !== '') {
          parameters[fieldKey] = fieldValue;
        }
      }
    }

    const values = this.valueMap(testKey);
    Object.entries(parameters).forEach(([fieldKey, fieldValue]) => {
      if (fieldValue === null || fieldValue === undefined) return;
      if (values.has(fieldKey)) return;

      values.set(fieldKey, fieldValue);
    });

    if (this.resolveTestAlias(row) === 'wbmTitration') {
      const setDefault = (key: string, defaultValue: string, ...legacyKeys: string[]): void => {
        if (values.get(key)?.trim()) {
          return;
        }

        const legacyValue = legacyKeys.map(legacyKey => values.get(legacyKey)?.trim()).find(Boolean);
        values.set(key, legacyValue || defaultValue);
      };

      setDefault('agNo3Concentration', '10000', 'wholeMudSilverNitrateConcentration', 'silverNitrateConcentration');
      setDefault('diluentTitratedVolume', '2.0', 'volumeOfDiluentTitratedMl');
      setDefault('flaskVolume', '100', 'volumeOfFlaskMl');

      const legacyBariteValue = values.get('bariteSharePoundsPerBarrel')?.trim();
      if (!values.get('bariteLbPerBbl')?.trim() && legacyBariteValue) {
        values.set('bariteLbPerBbl', legacyBariteValue);
      }
    }

    if (this.resolveTestAlias(row) === 'rheology') {
      const temperatures = this.extractRheologyTemperatures(parameters);
      this.rheologyTemperaturesByRow.set(
        testKey,
        temperatures.length > 0 ? temperatures : this.defaultRheologyTemperatures
      );
      return;
    }

    if (this.resolveTestAlias(row) === 'hphtRheology') {
      const readings = this.extractHphtRheologyReadings(parameters);
      const hasIndexedReadings = Object.keys(parameters).some(key => /^reading:\d+:temperature$/i.test(key));
      const configuredReadings = readings.length > 0
        ? readings
        : this.defaultHphtRheologyTemperatures.map((temperature, index) => ({ id: index + 1, temperature }));
      this.hphtRheologyTemperaturesByRow.set(testKey, configuredReadings);

      if (!hasIndexedReadings) {
        for (const reading of configuredReadings) {
          for (const keySuffix of this.hphtRheologyTemperatureKeySuffixes()) {
            const legacyKey = `t${reading.temperature}_${keySuffix}`;
            const indexedKey = this.hphtRheologyFieldKey(reading.id, keySuffix);
            if (values.has(legacyKey) && !values.has(indexedKey)) {
              values.set(indexedKey, values.get(legacyKey) ?? '');
            }
          }
        }
      }

      for (const reading of configuredReadings) {
        const pressureKey = this.hphtRheologyFieldKey(reading.id, 'pressure');
        if (!values.has(pressureKey)) {
          values.set(pressureKey, '0');
        }
      }

      return;
    }

    if (this.resolveTestAlias(row) === 'hphtFluidLoss' && !values.has('temperature')) {
      values.set('temperature', String(this.defaultHphtFluidLossTemperature));
    }

    if (this.resolveTestAlias(row) === 'hphtFluidLoss' && !values.has('pressure')) {
      values.set('pressure', String(this.defaultHphtFluidLossPressure));
    }

    if (this.resolveTestAlias(row) === 'ppt' && !this.hasAnyPptParameters(parameters)) {
      if (!values.has('temperature')) {
        values.set('temperature', String(this.defaultPptTemperature));
      }

      if (!values.has('discGrade')) {
        values.set('discGrade', String(this.defaultPptDiscGrade));
      }

      if (!values.has('pressure')) {
        values.set('pressure', String(this.defaultPptDifferentialPressure));
      }
    }

    if (this.resolveTestAlias(row) === 'apiFluidLoss' && !values.has('filtrateVolumeLimit')) {
      values.set('filtrateVolumeLimit', String(this.defaultApiFluidLossFiltrateVolumeLimit));
    }

    if (this.resolveTestAlias(row) === 'retort' && !values.has('sampleVolumeMl')) {
      values.set('sampleVolumeMl', String(this.defaultRetortSampleVolume));
    }

    if (this.resolveTestAlias(row) === 'staticAging' && !values.has('mudVolume')) {
      values.set('mudVolume', String(this.defaultStaticAgingMudVolume));
    }

    if (this.resolveTestAlias(row) === 'electricalStability') {
      this.electricalStabilityStoredReadingCountByRow.set(
        testKey,
        this.extractStoredElectricalStabilityReadingIndices(parameters).length
      );

      const readings = this.extractElectricalStabilityReadings(parameters);
      this.electricalStabilityReadingsByRow.set(testKey, readings);

      for (const reading of readings) {
        const idKey = this.electricalStabilityFieldKey(reading.index, 'id');
        const temperatureKey = this.electricalStabilityFieldKey(reading.index, 'temperature');

        if (!values.has(idKey)) {
          values.set(idKey, this.createElectricalStabilityReadingId());
        }

        if (!values.has(temperatureKey)) {
          values.set(temperatureKey, String(reading.temperature));
        }
      }

      const first = readings[0];
      if (first) {
        const firstTemperatureKey = this.electricalStabilityFieldKey(first.index, 'temperature');
        const firstVoltage1Key = this.electricalStabilityFieldKey(first.index, 'voltage1');
        const firstVoltage2Key = this.electricalStabilityFieldKey(first.index, 'voltage2');
        const firstVoltage3Key = this.electricalStabilityFieldKey(first.index, 'voltage3');

        if (!values.has('requestedTemperature') && values.has(firstTemperatureKey)) {
          values.set('requestedTemperature', values.get(firstTemperatureKey) ?? '');
        }

        if (!values.has('voltage1') && values.has(firstVoltage1Key)) {
          values.set('voltage1', values.get(firstVoltage1Key) ?? '');
        }

        if (!values.has('voltage2') && values.has(firstVoltage2Key)) {
          values.set('voltage2', values.get(firstVoltage2Key) ?? '');
        }

        if (!values.has('voltage3') && values.has(firstVoltage3Key)) {
          values.set('voltage3', values.get(firstVoltage3Key) ?? '');
        }

        if (!values.has(firstVoltage1Key) && values.has('voltage1')) {
          values.set(firstVoltage1Key, values.get('voltage1') ?? '');
        }

        if (!values.has(firstVoltage2Key) && values.has('voltage2')) {
          values.set(firstVoltage2Key, values.get('voltage2') ?? '');
        }

        if (!values.has(firstVoltage3Key) && values.has('voltage3')) {
          values.set(firstVoltage3Key, values.get('voltage3') ?? '');
        }
      }
    }
  }

  private applySalinityDefaults(testKey: string, row: OverviewRowDto): void {
    if (this.resolveTestAlias(row) !== 'salinity') {
      return;
    }

    const values = this.valueMap(testKey);
    if (!values.get('mudUsedVolume')?.trim()) {
      values.set('mudUsedVolume', '1');
    }

    const alkalineDemand = values.get('alkalineDemand')?.trim();
    const legacyAlkalineDemand = values.get('alkalineDemandVolume')?.trim();
    if (alkalineDemand) {
      return;
    }

    if (legacyAlkalineDemand) {
      values.set('alkalineDemand', legacyAlkalineDemand);
      return;
    }

    const previousAlkalineDemand = this.latestSalinityAlkalineDemand(testKey);
    if (previousAlkalineDemand) {
      values.set('alkalineDemand', previousAlkalineDemand);
    }
  }

  private applyWbmChemistryDefaults(testKey: string, row: OverviewRowDto): void {
    if (this.resolveTestAlias(row) !== 'wbmSalinity') {
      return;
    }

    const values = this.valueMap(testKey);
    const setDefault = (key: string, defaultValue: string, ...legacyKeys: string[]): void => {
      if (values.get(key)?.trim()) {
        return;
      }

      const legacyValue = legacyKeys.map(legacyKey => values.get(legacyKey)?.trim()).find(Boolean);
      values.set(key, legacyValue || defaultValue);
    };

    setDefault(
      'wholeMudSampleVolume',
      '1',
      'mudUsedVolume',
      'mudSampleVolume',
      'sampleWeight',
      'mudSampleWeight',
      'wholeMudSampleWeight'
    );
    setDefault('filtrateSampleVolume', '1');
    setDefault('wholeMudSilverNitrateConcentration', '10000', 'agNo3Concentration');
    setDefault('filtrateSilverNitrateConcentration', '10000');
    setDefault('wholeMudEdtaConcentration', '400', 'edtaConcentration');
    setDefault('filtrateEdtaConcentration', '400');

    values.set('wholeMudAcidConcentration', '1');
    values.set('filtrateAcidConcentration', '1');
  }

  private refreshWbmTitrationBariteDefaults(): void {
    this.overviewGroups.forEach((group, groupIndex) => {
      group.overviews.forEach((overview, overviewIndex) => {
        if (overview.type !== 'Tests') return;

        overview.rows.forEach((row, rowIndex) => {
          if (this.resolveTestAlias(row) !== 'wbmTitration') return;
          this.applyWbmTitrationBariteDefault(this.testKey(groupIndex, overviewIndex, rowIndex), row);
        });
      });
    });
  }

  private applyWbmTitrationBariteDefault(testKey: string, row: OverviewRowDto): void {
    if (this.resolveTestAlias(row) !== 'wbmTitration' || this.manuallyEditedWbmBariteRows.has(testKey)) {
      return;
    }

    const values = this.valueMap(testKey);
    const isAutoGenerated = this.autoGeneratedWbmBariteRows.has(testKey);
    const currentBarite = values.get('bariteLbPerBbl')?.trim();
    const currentBariteValue = this.finiteNumberOrNull(currentBarite);
    if (currentBarite && !isAutoGenerated && currentBariteValue !== 0) {
      return;
    }

    const targetChlorides = this.latestWbmChemistryTotalChlorides();
    const calculatedBarite = targetChlorides === null
      ? null
      : this.calculateWbmBariteLbPerBbl(targetChlorides);

    if (calculatedBarite === null) {
      if (isAutoGenerated) {
        values.delete('bariteLbPerBbl');
        this.autoGeneratedWbmBariteRows.delete(testKey);
      }
      return;
    }

    values.set('bariteLbPerBbl', calculatedBarite.toFixed(1));
    this.autoGeneratedWbmBariteRows.add(testKey);
  }

  private latestWbmChemistryTotalChlorides(): number | null {
    let latestValue: number | null = null;
    let latestTimestamp = Number.NEGATIVE_INFINITY;
    let latestOrder = -1;
    let traversalOrder = 0;

    this.overviewGroups.forEach((group, groupIndex) => {
      group.overviews.forEach((overview, overviewIndex) => {
        if (overview.type !== 'Tests') return;

        overview.rows.forEach((row, rowIndex) => {
          const testKey = this.testKey(groupIndex, overviewIndex, rowIndex);
          if (this.resolveTestAlias(row) !== 'wbmSalinity') {
            traversalOrder++;
            return;
          }

          const values = this.valueMap(testKey);
          const numberByKeys = (...keys: string[]): number | null => {
            for (const key of keys) {
              const parsedValue = this.finiteNumberOrNull(values.get(key));
              if (parsedValue !== null) return parsedValue;
            }

            return null;
          };
          const silverNitrateVolume = numberByKeys(
            'agNo3Volume',
            'wholeMudSilverNitrateVolume',
            'silverNitrateVolume'
          );
          const concentration = numberByKeys(
            'wholeMudSilverNitrateConcentration',
            'agNo3Concentration',
            'silverNitrateConcentration'
          );
          const mudUsedVolume = numberByKeys(
            'mudUsedVolume',
            'wholeMudSampleVolume',
            'mudSampleVolume',
            'sampleWeight',
            'mudSampleWeight',
            'wholeMudSampleWeight'
          );
          const calculatedValue = silverNitrateVolume !== null
            && concentration !== null
            && mudUsedVolume !== null
            && silverNitrateVolume >= 0
            && concentration > 0
            && mudUsedVolume > 0
            ? (silverNitrateVolume * concentration) / mudUsedVolume
            : null;
          const candidateValue = [
            calculatedValue,
            numberByKeys('wbmChemistry_wholeMudChloride'),
            numberByKeys('wholeMudChloride'),
            numberByKeys('wholeMudChlorides')
          ].find(value => value !== null && Number.isFinite(value) && value >= 0);
          if (candidateValue !== undefined) {
            const candidateTimestamp = this.rowTimestamp(row);
            if (
              candidateTimestamp > latestTimestamp
              || (candidateTimestamp === latestTimestamp && traversalOrder > latestOrder)
            ) {
              latestValue = candidateValue;
              latestTimestamp = candidateTimestamp;
              latestOrder = traversalOrder;
            }
          }

          traversalOrder++;
        });
      });
    });

    return latestValue;
  }

  private calculateWbmBariteLbPerBbl(targetChlorides: number): number | null {
    if (!Number.isFinite(targetChlorides) || targetChlorides < 0) {
      return null;
    }

    if (targetChlorides <= 250000) return 353;
    if (targetChlorides <= 450000) return 286;
    return 214;
  }

  private latestSalinityAlkalineDemand(excludedTestKey: string): string {
    let latestValue = '';
    let latestTimestamp = Number.NEGATIVE_INFINITY;
    let latestOrder = -1;
    let traversalOrder = 0;

    for (let groupIndex = 0; groupIndex < this.overviewGroups.length; groupIndex++) {
      const group = this.overviewGroups[groupIndex];

      for (let overviewIndex = 0; overviewIndex < group.overviews.length; overviewIndex++) {
        const overview = group.overviews[overviewIndex];
        if (overview.type !== 'Tests') continue;

        for (let rowIndex = 0; rowIndex < overview.rows.length; rowIndex++) {
          const candidateTestKey = this.testKey(groupIndex, overviewIndex, rowIndex);
          const candidateRow = overview.rows[rowIndex];
          const candidateValues = this.valueMap(candidateTestKey);
          const candidateAlkalineDemand = candidateValues.get('alkalineDemand');
          const candidateValue = candidateAlkalineDemand?.trim()
            ? candidateAlkalineDemand
            : candidateValues.get('alkalineDemandVolume') ?? '';

          if (
            candidateTestKey !== excludedTestKey
            && this.resolveTestAlias(candidateRow) === 'salinity'
            && candidateValue.trim()
          ) {
            const candidateTimestamp = this.rowTimestamp(candidateRow);
            if (
              candidateTimestamp > latestTimestamp
              || (candidateTimestamp === latestTimestamp && traversalOrder > latestOrder)
            ) {
              latestValue = candidateValue;
              latestTimestamp = candidateTimestamp;
              latestOrder = traversalOrder;
            }
          }

          traversalOrder++;
        }
      }
    }

    return latestValue;
  }

  private rowTimestamp(row: OverviewRowDto): number {
    const timestamp = Date.parse(row.outputCreatedOn ?? row.inputCreatedOn ?? '');
    return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY;
  }

  private computedFieldValue(testKey: string, fieldKey: string): string {
    const values = this.valueMap(testKey);

    if (fieldKey === 'densityPpg') {
      const sg = Number(values.get('specificGravity'));
      if (!Number.isFinite(sg) || sg <= 0) return '';
      return (sg * this.waterDensityPpg).toFixed(3);
    }

    if (fieldKey === 'averageVoltage') {
      const voltages = [values.get('voltage1'), values.get('voltage2'), values.get('voltage3')]
        .map(v => Number(v))
        .filter(v => Number.isFinite(v) && v >= 0);

      if (voltages.length === 0) return '';
      const avg = voltages.reduce((sum, current) => sum + current, 0) / voltages.length;
      return String(Math.trunc(avg));
    }

    const electricalStabilityAverageMatch = /^esReading(\d+)AverageVoltage$/i.exec(fieldKey);
    if (electricalStabilityAverageMatch) {
      const index = Number(electricalStabilityAverageMatch[1]);
      const voltages = [1, 2, 3]
        .map(voltageIndex => values.get(this.electricalStabilityFieldKey(index, `voltage${voltageIndex}`)))
        .map(v => Number(v))
        .filter(v => Number.isFinite(v) && v >= 0);

      if (voltages.length === 0) return '';
      const avg = voltages.reduce((sum, current) => sum + current, 0) / voltages.length;
      return String(Math.trunc(avg));
    }

    if (fieldKey === 'pptTotalVolume' || fieldKey === 'pptValue' || fieldKey === 'spurtVolume') {
      const firstTube = Number(values.get('filtrateVolume'));
      const secondTube = Number(values.get('totalVolume'));

      if (!Number.isFinite(firstTube) || !Number.isFinite(secondTube) || firstTube < 0 || secondTube < 0) {
        return '';
      }

      const totalVolume = firstTube + secondTube;

      if (fieldKey === 'pptTotalVolume') {
        return totalVolume.toFixed(3);
      }

      if (fieldKey === 'pptValue') {
        return (totalVolume * 2).toFixed(3);
      }

      const multipliedFirstVolume = firstTube * 2;
      const spurt = multipliedFirstVolume < totalVolume ? 0 : 2 * (multipliedFirstVolume - totalVolume);
      return spurt.toFixed(3);
    }

    if (fieldKey === 'hphtFullFiltrateVolume') {
      const filtrateVolume = Number(values.get('filtrate30Min'));
      if (!Number.isFinite(filtrateVolume) || filtrateVolume < 0) {
        return '';
      }

      return (filtrateVolume * 2).toFixed(3);
    }

    if (fieldKey.startsWith('retort_')) {
      const waterDensityInMl = 1;
      const sampleWeight = this.safeNumber(values.get('cellMudWeight')) - this.safeNumber(values.get('cellTareWeight'));
      const cellAfter = this.safeNumber(values.get('cellAfterWeight'));
      const cellTare = this.safeNumber(values.get('cellTareWeight'));
      const solidsWeight = Math.max(cellAfter - cellTare, 0);
      const totalVolume = this.safeNumber(values.get('totalVolume'));
      const waterVolume = this.safeNumber(values.get('waterVolume'));
      const nafVolume = Math.max(totalVolume - waterVolume, 0);
      const measuredSpecificGravity = this.mudCheckMudWeightSpecificGravity(testKey);
      const measuredDensityPpg = measuredSpecificGravity * this.waterDensityPpg;
      const mudVolume = measuredSpecificGravity > 0 ? sampleWeight / measuredSpecificGravity : 0;

      const jTubeGrossWeight = this.firstFinitePositive(
        this.safeNumber(values.get('jTubeGrossWeight')),
        this.safeNumber(values.get('mudSampleWeight'))
      );
      const jTubeTareWeight = this.safeNumber(values.get('jTubeTareWeight'));
      const totalFluidWeight = Math.max(jTubeGrossWeight - jTubeTareWeight, 0);
      const nafWeight = Math.max(totalFluidWeight - waterVolume * waterDensityInMl, 0);
      const nafSpecificGravity = nafVolume > 0 ? nafWeight / nafVolume : 0;

      const nafOfTotal = mudVolume > 0 ? (100 * nafVolume) / mudVolume : 0;
      const waterOfTotal = mudVolume > 0 ? (100 * waterVolume) / mudVolume : 0;
      const nafOfLiquid = nafVolume + waterVolume > 0 ? (100 * nafVolume) / (nafVolume + waterVolume) : 0;
      const waterOfLiquid = nafVolume + waterVolume > 0 ? (100 * waterVolume) / (nafVolume + waterVolume) : 0;
      const solidsVolumePercent = 100 - nafOfTotal - waterOfTotal;

      const baseFluidSpecificGravity = this.baseFluidSpecificGravity ?? 0;
      const requiredQaInputs = [
        this.numericValue(testKey, 'cellMudWeight'),
        this.numericValue(testKey, 'cellTareWeight'),
        this.numericValue(testKey, 'cellAfterWeight'),
        this.numericValue(testKey, 'totalVolume'),
        this.numericValue(testKey, 'waterVolume')
      ];
      const errorMargin = requiredQaInputs.some(value => value === null)
        ? ''
        : (sampleWeight > 0
          ? 100 * (solidsWeight + waterVolume * waterDensityInMl + nafVolume * baseFluidSpecificGravity) / sampleWeight
          : 0).toFixed(3);

      switch (fieldKey) {
        case 'retort_measuredSpecificGravity':
          return measuredSpecificGravity > 0 ? measuredSpecificGravity.toFixed(3) : '';
        case 'retort_measuredDensityPpg':
          return measuredDensityPpg > 0 ? measuredDensityPpg.toFixed(3) : '';
        case 'retort_sampleWeight':
          return Number.isFinite(sampleWeight) ? sampleWeight.toFixed(3) : '';
        case 'retort_mudVolume':
          return mudVolume > 0 ? mudVolume.toFixed(3) : '';
        case 'retort_solidsWeight':
          return solidsWeight.toFixed(3);
        case 'retort_nafVolume':
          return nafVolume.toFixed(3);
        case 'retort_errorMargin':
          return errorMargin;
        case 'retort_nafOfTotal':
          return nafOfTotal.toFixed(3);
        case 'retort_nafOfLiquid':
          return nafOfLiquid.toFixed(3);
        case 'retort_waterOfTotal':
          return waterOfTotal.toFixed(3);
        case 'retort_waterOfLiquid':
          return waterOfLiquid.toFixed(3);
        case 'retort_totalFluidWeight':
          return totalFluidWeight.toFixed(3);
        case 'retort_nafWeight':
          return nafWeight.toFixed(3);
        case 'retort_nafSpecificGravity':
          return nafSpecificGravity > 0 ? nafSpecificGravity.toFixed(3) : '';
        case 'retort_solidsVolumePercent':
          return solidsVolumePercent.toFixed(3);
        default:
          return '';
      }
    }

    if (fieldKey.startsWith('wbmTitration_')) {
      const mudSpecificGravity = this.firstFinitePositive(
        this.mudCheckMudWeightSpecificGravity(testKey),
        this.safeNumber(values.get('measuredSpecificGravity')),
        this.safeNumber(values.get('specificGravity')),
        this.safeNumber(values.get('mudSpecificGravity'))
      );      
      const mudSampleWeight = this.safeNumber(values.get('mudSampleWeight'));
      const mudSampleVolumeFromInput = this.safeNumber(values.get('mudSampleVolume'));
      const mudSampleVolume = mudSpecificGravity > 0 && mudSampleWeight > 0
        ? mudSampleWeight / mudSpecificGravity
        : mudSampleVolumeFromInput;

      const diluentVolume = this.firstFinitePositive(
        this.safeNumber(values.get('diluentTitratedVolume')),
        this.safeNumber(values.get('volumeOfDiluentTitratedMl'))
      );
      const flaskVolume = this.firstFinitePositive(
        this.safeNumber(values.get('flaskVolume')),
        this.safeNumber(values.get('volumeOfFlaskMl'))
      );
      const silverNitrateVolume = values.get('agNo3Volume')?.trim()
        ? this.safeNumber(values.get('agNo3Volume'))
        : this.firstFinitePositive(
          this.safeNumber(values.get('wholeMudSilverNitrateVolume')),
          this.safeNumber(values.get('silverNitrateVolume'))
        );
      const silverNitrateConcentration = this.firstFinitePositive(
        this.safeNumber(values.get('agNo3Concentration')),
        this.safeNumber(values.get('wholeMudSilverNitrateConcentration')),
        this.safeNumber(values.get('silverNitrateConcentration'))
      );
      const bariteLbPerBbl = this.firstFinitePositive(
        this.safeNumber(values.get('bariteLbPerBbl')),
        this.safeNumber(values.get('bariteSharePoundsPerBarrel'))
      );

      const chloridesInBlend = diluentVolume > 0 && silverNitrateConcentration > 0
        ? (silverNitrateVolume * silverNitrateConcentration) / diluentVolume
        : Number.NaN;
      const bariteShareGg = mudSpecificGravity > 0
        ? bariteLbPerBbl / (1000 * mudSpecificGravity)
        : Number.NaN;
      const bariteVolumeShare = bariteLbPerBbl >= 0
        ? bariteLbPerBbl / (this.bariteDensity * 350)
        : Number.NaN;
      const bariteInFlaskWeight = mudSampleWeight > 0 && Number.isFinite(bariteShareGg)
        ? mudSampleWeight * bariteShareGg
        : Number.NaN;
      const bariteInFlaskVolume = Number.isFinite(bariteInFlaskWeight) && bariteInFlaskWeight >= 0
        ? bariteInFlaskWeight / this.bariteDensity
        : Number.NaN;
      const volumeMinusBarite = flaskVolume > 0 && Number.isFinite(bariteInFlaskVolume)
        ? flaskVolume - bariteInFlaskVolume
        : Number.NaN;
      const dilutionRatio = Number.isFinite(volumeMinusBarite) && mudSampleVolume > 0
        ? volumeMinusBarite / mudSampleVolume
        : Number.NaN;

      const chloridesCorrection = Number.isFinite(chloridesInBlend) && chloridesInBlend > 0
        ? ((chloridesInBlend * 2) - ((25 + diluentVolume + silverNitrateVolume) * 0.005)) / (chloridesInBlend * 2)
        : Number.NaN;

      const totalChlorides = Number.isFinite(dilutionRatio) && Number.isFinite(chloridesInBlend) && Number.isFinite(chloridesCorrection)
        ? dilutionRatio * chloridesInBlend * chloridesCorrection
        : Number.NaN;

      switch (fieldKey) {
        case 'wbmTitration_mudSpecificGravity':
          return mudSpecificGravity > 0 ? mudSpecificGravity.toFixed(3) : '';
        case 'wbmTitration_mudDensityPpg':
          return mudSpecificGravity > 0 ? (mudSpecificGravity * this.waterDensityPpg).toFixed(3) : '';
        case 'wbmTitration_mudSampleVolume':
          return mudSampleVolume > 0 ? mudSampleVolume.toFixed(3) : '';
        case 'wbmTitration_chloridesInBlend':
          return Number.isFinite(chloridesInBlend) ? chloridesInBlend.toFixed(3) : '';
        case 'wbmTitration_bariteDensity':
          return this.bariteDensity.toFixed(3);
        case 'wbmTitration_bariteShareGg':
          return Number.isFinite(bariteShareGg) ? bariteShareGg.toFixed(3) : '';
        case 'wbmTitration_bariteVolumeShare':
          return Number.isFinite(bariteVolumeShare) ? bariteVolumeShare.toFixed(3) : '';
        case 'wbmTitration_bariteInFlaskWeight':
          return Number.isFinite(bariteInFlaskWeight) ? bariteInFlaskWeight.toFixed(3) : '';
        case 'wbmTitration_bariteInFlaskVolume':
          return Number.isFinite(bariteInFlaskVolume) ? bariteInFlaskVolume.toFixed(3) : '';
        case 'wbmTitration_volumeMinusBarite':
          return Number.isFinite(volumeMinusBarite) ? volumeMinusBarite.toFixed(3) : '';
        case 'wbmTitration_dilutionRatio':
          return Number.isFinite(dilutionRatio) ? dilutionRatio.toFixed(3) : '';
        case 'wbmTitration_totalChlorides':
          return Number.isFinite(totalChlorides) ? totalChlorides.toFixed(0) : '';
        default:
          return '';
      }
    }

    if (fieldKey.startsWith('wbmChemistry_')) {
      const wholeMudSampleVolume = this.firstFinitePositive(
        this.safeNumber(values.get('wholeMudSampleVolume')),
        this.safeNumber(values.get('mudUsedVolume')),
        this.safeNumber(values.get('mudSampleVolume')),
        this.safeNumber(values.get('sampleWeight')),
        this.safeNumber(values.get('mudSampleWeight')),
        this.safeNumber(values.get('wholeMudSampleWeight'))
      );
      const filtrateSampleVolume = this.safeNumber(values.get('filtrateSampleVolume'));
      const wholeMudAcidVolume = this.safeNumber(values.get('wholeMudAcidVolume'));
      const filtrateAcidPfVolume = this.safeNumber(values.get('filtrateAcidPfVolume'));
      const filtrateAcidMfVolume = this.safeNumber(values.get('filtrateAcidMfVolume'));
      const wholeMudSilverNitrateVolume = this.firstFinitePositive(
        this.safeNumber(values.get('wholeMudSilverNitrateVolume')),
        this.safeNumber(values.get('agNo3Volume')),
        this.safeNumber(values.get('silverNitrateVolume'))
      );
      const filtrateSilverNitrateVolume = this.safeNumber(values.get('filtrateSilverNitrateVolume'));
      const wholeMudEdtaVolume = this.safeNumber(values.get('wholeMudEdtaVolume'));
      const filtrateEdtaTotalHardnessVolume = this.safeNumber(values.get('filtrateEdtaTotalHardnessVolume'));
      const filtrateEdtaCalciumVolume = this.safeNumber(values.get('filtrateEdtaCalciumVolume'));
      const wholeMudSilverNitrateConcentration = this.firstFinitePositive(
        this.safeNumber(values.get('wholeMudSilverNitrateConcentration')),
        this.safeNumber(values.get('agNo3Concentration')),
        this.safeNumber(values.get('silverNitrateConcentration'))
      );
      const filtrateSilverNitrateConcentration = this.firstFinitePositive(
        this.safeNumber(values.get('filtrateSilverNitrateConcentration')),
        wholeMudSilverNitrateConcentration
      );
      const wholeMudEdtaConcentration = this.firstFinitePositive(
        this.safeNumber(values.get('wholeMudEdtaConcentration')),
        this.safeNumber(values.get('edtaConcentration'))
      );
      const filtrateEdtaConcentration = this.firstFinitePositive(
        this.safeNumber(values.get('filtrateEdtaConcentration')),
        wholeMudEdtaConcentration
      );

      const mudAlkalinity = wholeMudSampleVolume > 0
        ? wholeMudAcidVolume / wholeMudSampleVolume
        : Number.NaN;
      const hasFiltrateAlkalinity = Boolean(
        values.get('filtrateAcidPfVolume')?.trim() || values.get('filtrateAcidMfVolume')?.trim()
      );
      const filtrateAlkalinityPf = hasFiltrateAlkalinity ? filtrateAcidPfVolume.toFixed(2) : '';
      const filtrateAlkalinityMf = hasFiltrateAlkalinity ? filtrateAcidMfVolume.toFixed(2) : '';
      const filtrateAlkalinity = hasFiltrateAlkalinity
        ? `${filtrateAlkalinityPf}/${filtrateAlkalinityMf}`
        : '';
      const filtrateChlorides = filtrateSampleVolume > 0 && filtrateSilverNitrateConcentration > 0
        ? (filtrateSilverNitrateVolume * filtrateSilverNitrateConcentration) / filtrateSampleVolume
        : Number.NaN;
      const filtrateTotalHardness = filtrateSampleVolume > 0 && filtrateEdtaConcentration > 0
        ? (filtrateEdtaTotalHardnessVolume * filtrateEdtaConcentration) / filtrateSampleVolume
        : Number.NaN;
      const filtrateCalcium = filtrateSampleVolume > 0 && filtrateEdtaConcentration > 0
        ? (filtrateEdtaCalciumVolume * filtrateEdtaConcentration) / filtrateSampleVolume
        : Number.NaN;
      const filtrateMagnesium = Number.isFinite(filtrateTotalHardness) && Number.isFinite(filtrateCalcium)
        ? Math.max(0, 0.6 * (filtrateTotalHardness - filtrateCalcium))
        : Number.NaN;
      const wholeMudChloride = wholeMudSampleVolume > 0 && wholeMudSilverNitrateConcentration > 0
        ? (wholeMudSilverNitrateVolume * wholeMudSilverNitrateConcentration) / wholeMudSampleVolume
        : Number.NaN;
      const wholeMudCalcium = wholeMudSampleVolume > 0 && wholeMudEdtaConcentration > 0
        ? (wholeMudEdtaVolume * wholeMudEdtaConcentration) / wholeMudSampleVolume
        : Number.NaN;

      switch (fieldKey) {
        case 'wbmChemistry_mudAlkalinity':
          return Number.isFinite(mudAlkalinity) ? mudAlkalinity.toFixed(2) : '';
        case 'wbmChemistry_filtrateAlkalinity':
          return filtrateAlkalinity;
        case 'wbmChemistry_filtrateAlkalinityPf':
          return filtrateAlkalinityPf;
        case 'wbmChemistry_filtrateAlkalinityMf':
          return filtrateAlkalinityMf;
        case 'wbmChemistry_filtrateChlorides':
          return Number.isFinite(filtrateChlorides) ? filtrateChlorides.toFixed(0) : '';
        case 'wbmChemistry_filtrateTotalHardness':
          return Number.isFinite(filtrateTotalHardness) ? filtrateTotalHardness.toFixed(0) : '';
        case 'wbmChemistry_filtrateCalcium':
          return Number.isFinite(filtrateCalcium) ? filtrateCalcium.toFixed(0) : '';
        case 'wbmChemistry_filtrateMagnesium':
          return Number.isFinite(filtrateMagnesium) ? filtrateMagnesium.toFixed(1) : '';
        case 'wbmChemistry_wholeMudChloride':
          return Number.isFinite(wholeMudChloride) ? wholeMudChloride.toFixed(0) : '';
        case 'wbmChemistry_wholeMudCalcium':
          return Number.isFinite(wholeMudCalcium) ? wholeMudCalcium.toFixed(0) : '';
        default:
          return '';
      }
    }

    if (fieldKey.startsWith('salinity_')) {
      const backendComputed = values.get(fieldKey);
      if (backendComputed && backendComputed.trim().length > 0) {
        return backendComputed;
      }

      const mudUsedVolume = this.firstFinitePositive(
        this.safeNumber(values.get('mudUsedVolume')),
        this.safeNumber(values.get('wholeMudSampleVolume')),
        this.safeNumber(values.get('mudSampleVolume'))
      );
      const alkalineDemand = this.safeNumber(values.get('alkalineDemand'));
      const acidVolume = this.firstFinitePositive(
        this.safeNumber(values.get('acidVolume')),
        this.safeNumber(values.get('wholeMudAcidVolume'))
      );
      const silverNitrateVolume = this.firstFinitePositive(
        this.safeNumber(values.get('agNo3Volume')),
        this.safeNumber(values.get('wholeMudSilverNitrateVolume'))
      );
      const edtaVolume = this.firstFinitePositive(
        this.safeNumber(values.get('edtaVolume')),
        this.safeNumber(values.get('wholeMudEdtaVolume'))
      );
      const measuredSpecificGravity = this.firstFinitePositive(
        this.mudCheckMudWeightSpecificGravity(testKey),
        this.safeNumber(values.get('measuredSpecificGravity')),
        this.safeNumber(values.get('specificGravity'))
      );

      if (fieldKey === 'salinity_mudWeight') {
        return measuredSpecificGravity > 0 ? measuredSpecificGravity.toFixed(3) : '';
      }

      if (mudUsedVolume <= 0) {
        return fieldKey === 'salinity_waterPhaseSalinity' ? '' : '';
      }

      const agNo3Concentration = this.firstFinitePositive(
        this.safeNumber(values.get('wholeMudSilverNitrateConcentration')),
        this.safeNumber(values.get('agNo3Concentration')),
        this.safeNumber(values.get('silverNitrateConcentration')),
        10000
      );

      const edtaConcentration = this.firstFinitePositive(
        this.safeNumber(values.get('edtaConcentration')),
        this.safeNumber(values.get('wholeMudEdtaConcentration')),
        4000
      );

      const titration = calculateNafChemistryTitration({
        mudUsedVolume,
        alkalineDemand,
        acidVolume,
        silverNitrateVolume,
        edtaVolume,
        silverNitrateConcentration: agNo3Concentration,
        edtaConcentration
      });
      if (!titration) return '';

      const hasSilverNitrateVolume = this.finiteNumberOrNull(values.get('agNo3Volume')) !== null
        || this.finiteNumberOrNull(values.get('wholeMudSilverNitrateVolume')) !== null;
      const hasEdtaVolume = this.finiteNumberOrNull(values.get('edtaVolume')) !== null
        || this.finiteNumberOrNull(values.get('wholeMudEdtaVolume')) !== null;

      switch (fieldKey) {
        case 'salinity_nafMudAlkalinity':
          return titration.nafMudAlkalinity.toFixed(3);
        case 'salinity_limeContent':
          return titration.limeContent.toFixed(3);
        case 'salinity_edtaToMudRatio':
          return titration.edtaToMudRatio.toFixed(3);
        case 'salinity_agNo3ToMudRatio':
          return titration.silverNitrateToMudRatio.toFixed(3);
        case 'salinity_acidToMudRatio':
          return titration.acidToMudRatio.toFixed(3);
        case 'salinity_wholeMudChlorides':
          return titration.wholeMudChlorides !== null ? titration.wholeMudChlorides.toFixed(0) : '';
        case 'salinity_wholeMudCalcium':
          return titration.wholeMudCalcium !== null ? titration.wholeMudCalcium.toFixed(0) : '';
        case 'salinity_waterPhaseSalinity': {
          if (!hasSilverNitrateVolume || !hasEdtaVolume) return '';
          const waterPhaseSalinity = calculateNafWaterPhaseSalinity(
            titration.silverNitrateToMudRatio,
            titration.edtaToMudRatio,
            this.firstRetortVolumePercent('water')
          );
          return waterPhaseSalinity !== null && Number.isFinite(waterPhaseSalinity)
            ? waterPhaseSalinity.toFixed(2)
            : '';
        }
        case 'salinity_correctedSolidsVolumePercent': {
          if (!hasSilverNitrateVolume || !hasEdtaVolume) return '';
          const correctedSolidsVolumePercent = calculateNafCorrectedSolidsVolumePercent(
            titration.silverNitrateToMudRatio,
            titration.edtaToMudRatio,
            this.firstRetortVolumePercent('naf'),
            this.firstRetortVolumePercent('water')
          );
          return correctedSolidsVolumePercent !== null && Number.isFinite(correctedSolidsVolumePercent)
            ? correctedSolidsVolumePercent.toFixed(2)
            : '';
        }
        case 'salinity_lowGravitySolidsVolumePercent': {
          if (!hasSilverNitrateVolume || !hasEdtaVolume) return '';
          const solidsComposition = calculateNafSolidsComposition(
            titration.silverNitrateToMudRatio,
            titration.edtaToMudRatio,
            this.firstRetortVolumePercent('naf'),
            this.firstRetortVolumePercent('water'),
            measuredSpecificGravity,
            this.firstConstantsOverviewValue('Base Fluid SG') ?? this.baseFluidSpecificGravity,
            this.firstConstantsOverviewValue('Low Gravity Solids SG') ?? this.lowGravitySolidsSpecificGravity ?? null,
            this.firstConstantsOverviewValue('Barite SG') ?? this.bariteSpecificGravity ?? null
          );
          return solidsComposition !== null
            ? solidsComposition.lowGravitySolidsVolumePercent.toFixed(2)
            : '';
        }
        default:
          return '';
      }
    }

    if (fieldKey === 'd10' || fieldKey === 'd50' || fieldKey === 'd90') {
      const percentileKeyBySummary: Record<string, string> = {
        d10: 'percentile10_size',
        d50: 'percentile50_size',
        d90: 'percentile90_size'
      };

      const percentileKey = percentileKeyBySummary[fieldKey];
      const percentileValue = values.get(percentileKey);
      if (percentileValue && percentileValue.trim().length > 0) {
        return percentileValue;
      }

      // Backward compatibility with older PSD payloads that stored direct D-values.
      return values.get(fieldKey) ?? '';
    }

    if (fieldKey === 'staticAging_mudDensityPpg') {
      const mudSpecificGravity = this.mudCheckMudWeightSpecificGravity(testKey);
      return mudSpecificGravity > 0 ? (mudSpecificGravity * this.waterDensityPpg).toFixed(3) : '';
    }

    const staticAgingDensitySourceNames: Record<string, string[]> = {
      staticAging_topDensity: ['Top Density', 'Top Pycnometer'],
      staticAging_middleDensity: ['Middle Density', 'Middle Pycnometer'],
      staticAging_bottomDensity: ['Bottom Density', 'Bottom Pycnometer'],
      staticAging_asaCombinedDensity: ['Combined Density', 'Combined Pycnometer']
    };
    const staticAgingDensitySourceName = staticAgingDensitySourceNames[fieldKey];
    if (staticAgingDensitySourceName) {
      const density = this.staticAgingNamedDensity(testKey, staticAgingDensitySourceName);
      return density > 0 ? density.toFixed(3) : '';
    }

    if (fieldKey.startsWith('staticAging_')) {
      const initialDensity = this.safeNumber(this.computedFieldValue(testKey, 'staticAging_mudDensityPpg'));
      const mudVolume = this.safeNumber(values.get('mudVolume'));
      const columnMass = this.safeNumber(values.get('columnMass'));
      const shelfMass = this.safeNumber(values.get('shelfMass'));
      const weightsMass = this.safeNumber(values.get('weightsMass'));
      const penetration = this.safeNumber(values.get('penetration'));
      const freeFluid = this.safeNumber(values.get('freeFluid'));
      const topDensity = this.safeNumber(this.computedFieldValue(testKey, 'staticAging_topDensity'));
      const bottomDensity = this.safeNumber(this.computedFieldValue(testKey, 'staticAging_bottomDensity'));
      const totalMass = columnMass + shelfMass + weightsMass;
      const initialSg = initialDensity > 0 ? initialDensity / this.waterDensityPpg : 0;

      switch (fieldKey) {
        case 'staticAging_mudSG':
          const mudCheckSpecificGravity = this.mudCheckMudWeightSpecificGravity(testKey);
          return mudCheckSpecificGravity > 0 ? mudCheckSpecificGravity.toFixed(3) : '';
        case 'staticAging_totalMass':
          return totalMass > 0 ? totalMass.toFixed(2) : '';
        case 'staticAging_freeFluidVolumePercent':
          return mudVolume > 0 && freeFluid > 0 ? ((freeFluid / mudVolume) * 100).toFixed(2) : '';
        case 'staticAging_shearStrength':
          return penetration > 0 && totalMass > 0 && initialSg > 0
            ? ((3.615 * totalMass / penetration) - 0.256 * initialSg).toFixed(0)
            : '';
        case 'staticAging_sagFactor':
          return topDensity + bottomDensity > 0
            ? (bottomDensity / (topDensity + bottomDensity)).toFixed(2)
            : '';
        case 'staticAging_sagIndex':
          return initialDensity > 0 && bottomDensity > 0
            ? (bottomDensity / (2 * initialDensity)).toFixed(2)
            : '';
        default:
          return '';
      }
    }

    return '';
  }

  private finiteNumberOrNull(value?: string | null): number | null {
    if (value === undefined || value === null || value.trim() === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private safeNumber(value?: string | null): number {
    if (!value) return 0;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private firstFinitePositive(...values: number[]): number {
    for (const value of values) {
      if (Number.isFinite(value) && value > 0) {
        return value;
      }
    }

    return 0;
  }

  private collectSpreadsheetParameters(testKey: string, spreadsheet: Worksheet): {
    inputParameters: Record<string, string | null>;
    outputParameters: Record<string, string | null>;
  } {
    const storedValues = this.valueMap(testKey);
    const cells: Record<string, CellDefinition> = {};

    for (const [address, cell] of Object.entries(spreadsheet.cells ?? {})) {
      const storedValue = storedValues.get(address);
      if (cell.type === 'formula') {
        // A formula edit is stored as the raw formula in the test input map.
        // Use it when present; otherwise use the definition's formula value.
        const formula = storedValue?.trim().startsWith('=') ? storedValue : String(cell.value);
        cells[address] = { type: 'formula', value: formula };
        continue;
      }

      if (cell.type === 'text') {
        // Text cells are worksheet labels, not values entered for the test.
        cells[address] = { ...cell };
        continue;
      }

      if (storedValues.has(address) && storedValue !== '') {
        const numericValue = Number(storedValue);
        cells[address] = {
          type: 'number',
          value: Number.isFinite(numericValue) ? numericValue : '',
        };
      } else {
        cells[address] = { type: 'number', value: '' };
      }
    }

    const calculation = this.spreadsheetService.recalculate({
      rows: spreadsheet.rows,
      columns: spreadsheet.columns,
      cells,
    });
    const inputParameters: Record<string, string | null> = {};
    const outputParameters: Record<string, string | null> = {};

    for (const [address, cell] of Object.entries(cells)) {
      if (cell.type === 'formula') {
        outputParameters[address] = calculation.errors[address]
          ? null
          : this.parameterValue(calculation.values[address]);
      } else if (cell.type === 'number') {
        inputParameters[address] = this.parameterValue(cell.value);
      } else if (cell.type === 'text') {
        outputParameters[address] = this.parameterValue(cell.value);
      }
    }

    return { inputParameters, outputParameters };
  }

  private parameterValue(value: unknown): string | null {
    return value === null || value === undefined ? null : String(value);
  }

  private collectParameters(testKey: string, testLabel: string): Record<string, string | null> {
    const alias = this.testAlias(testLabel);
    const values = this.valueMap(testKey);
    const parameters: Record<string, string | null> = {};

    if (alias === 'rheology') {
      for (const temperature of this.rheologyTemperatures(testKey)) {
        const f2SpringKey = this.rheologyFieldKey(temperature, 'f2Spring');
        if (values.has(f2SpringKey)) {
          parameters[f2SpringKey] = values.get(f2SpringKey) ?? null;
        }

        for (const row of this.rheologyRows) {
          const key = this.rheologyFieldKey(temperature, row.key);
          if (!values.has(key)) continue;
          parameters[key] = values.get(key) ?? null;
        }
      }
      return parameters;
    }

    if (alias === 'hphtRheology') {
      for (const reading of this.hphtRheologyTemperatures(testKey)) {
        parameters[this.hphtRheologyFieldKey(reading.id, 'temperature')] = String(reading.temperature);

        const pressureKey = this.hphtRheologyFieldKey(reading.id, 'pressure');
        if (values.has(pressureKey)) {
          parameters[pressureKey] = values.get(pressureKey) ?? null;
        }

        for (const row of this.hphtRheologyRows) {
          const key = this.hphtRheologyFieldKey(reading.id, row.key);
          if (!values.has(key)) continue;
          parameters[key] = values.get(key) ?? null;
        }
      }
      if (values.has('equipmentName')) {
        parameters['equipmentName'] = values.get('equipmentName') ?? null;
      }
      return parameters;
    }

    if (alias === 'pycnometer') {
      parameters['profileId'] = values.get('profileId') ?? null;
      parameters['profileName'] = values.get('profileName') ?? null;
      parameters['referenceVolume'] = values.get('referenceVolume') ?? null;
      parameters['cellVolume'] = values.get('cellVolume') ?? null;
      parameters['sampleWeight'] = values.get('sampleWeight') ?? null;

      for (const run of this.pycnometerRunIndices) {
        const p1Key = this.pycnometerRunKey(run, 'p1');
        const p2Key = this.pycnometerRunKey(run, 'p2');
        if (values.has(p1Key)) parameters[p1Key] = values.get(p1Key) ?? null;
        if (values.has(p2Key)) parameters[p2Key] = values.get(p2Key) ?? null;
      }

      return parameters;
    }

    if (alias === 'density') {
      parameters['specificGravity'] = values.get('specificGravity') ?? null;
      parameters['densityPpg'] = this.computedFieldValue(testKey, 'densityPpg') || null;
      return parameters;
    }

    if (alias === 'electricalStability') {
      const readingIndices = this.electricalStabilityReadingIndices(testKey);
      for (const index of readingIndices) {
        const idKey = this.electricalStabilityFieldKey(index, 'id');
        const temperatureKey = this.electricalStabilityFieldKey(index, 'temperature');
        const voltage1Key = this.electricalStabilityFieldKey(index, 'voltage1');
        const voltage2Key = this.electricalStabilityFieldKey(index, 'voltage2');
        const voltage3Key = this.electricalStabilityFieldKey(index, 'voltage3');

        if (values.has(idKey)) {
          parameters[idKey] = values.get(idKey) ?? null;
        }

        if (values.has(temperatureKey)) {
          parameters[temperatureKey] = values.get(temperatureKey) ?? null;
        }

        if (values.has(voltage1Key)) {
          parameters[voltage1Key] = values.get(voltage1Key) ?? null;
        }

        if (values.has(voltage2Key)) {
          parameters[voltage2Key] = values.get(voltage2Key) ?? null;
        }

        if (values.has(voltage3Key)) {
          parameters[voltage3Key] = values.get(voltage3Key) ?? null;
        }
      }

      const firstIndex = readingIndices[0];
      if (firstIndex !== undefined) {
        parameters['requestedTemperature'] = values.get(this.electricalStabilityFieldKey(firstIndex, 'temperature')) ?? null;
        parameters['voltage1'] = values.get(this.electricalStabilityFieldKey(firstIndex, 'voltage1')) ?? null;
        parameters['voltage2'] = values.get(this.electricalStabilityFieldKey(firstIndex, 'voltage2')) ?? null;
        parameters['voltage3'] = values.get(this.electricalStabilityFieldKey(firstIndex, 'voltage3')) ?? null;
      }

      if (values.has('equipmentName')) {
        parameters['equipmentName'] = values.get('equipmentName') ?? null;
      }

      return parameters;
    }

    const sections = alias ? TEST_DETAIL_SCHEMAS[alias] ?? [] : [];
    for (const section of sections) {
      for (const field of section.fields) {
        if (field.type === 'computed') continue;
        if (!values.has(field.key)) continue;
        parameters[field.key] = values.get(field.key) ?? null;
      }
    }

    if (alias === 'wbmSalinity') {
      parameters['wholeMudAcidConcentration'] = '1';
      parameters['filtrateAcidConcentration'] = '1';
    }

    return parameters;
  }

  private collectSavePayload(testKey: string, row: OverviewRowDto): {
    inputParameters: Record<string, string | null>;
    outputParameters: Record<string, string | null> | null;
  } {
    const alias = this.resolveTestAlias(row);
    const values = this.valueMap(testKey);

    if (alias === 'rheology') {
      const inputParameters: Record<string, string | null> = {};
      const outputParameters: Record<string, string | null> = {};

      for (const temperature of this.rheologyTemperatures(testKey)) {
        const pressureKey = this.rheologyFieldKey(temperature, 'pressure');
        if (values.has(pressureKey)) {
          inputParameters[pressureKey] = values.get(pressureKey) ?? null;
        }

        for (const row of this.rheologyRows) {
          const key = this.rheologyFieldKey(temperature, row.key);
          if (!values.has(key)) continue;
          outputParameters[key] = values.get(key) ?? null;
        }

        const f2SpringKey = this.rheologyFieldKey(temperature, 'f2Spring');
        if (values.has(f2SpringKey)) {
          outputParameters[f2SpringKey] = values.get(f2SpringKey) ?? null;
        }
      }

      return {
        inputParameters,
        outputParameters
      };
    }

    if (alias === 'hphtRheology') {
      const inputParameters: Record<string, string | null> = {};
      const outputParameters: Record<string, string | null> = {};

      for (const reading of this.hphtRheologyTemperatures(testKey)) {
        const temperatureKey = this.hphtRheologyFieldKey(reading.id, 'temperature');
        inputParameters[temperatureKey] = String(reading.temperature);

        const pressureKey = this.hphtRheologyFieldKey(reading.id, 'pressure');
        if (values.has(pressureKey)) {
          inputParameters[pressureKey] = values.get(pressureKey) ?? null;
        }

        for (const row of this.hphtRheologyRows) {
          const key = this.hphtRheologyFieldKey(reading.id, row.key);
          if (!values.has(key)) continue;
          outputParameters[key] = values.get(key) ?? null;
        }
      }

      if (values.has('equipmentName')) {
        outputParameters['equipmentName'] = values.get('equipmentName') ?? null;
      }

      return {
        inputParameters,
        outputParameters
      };
    }

    if (alias === 'pycnometer') {
      const outputParameters: Record<string, string | null> = {
        profileId: values.get('profileId') ?? null,
        profileName: values.get('profileName') ?? null,
        referenceVolume: values.get('referenceVolume') ?? null,
        cellVolume: values.get('cellVolume') ?? null,
        sampleWeight: values.get('sampleWeight') ?? null
      };

      for (const run of this.pycnometerRunIndices) {
        outputParameters[this.pycnometerRunKey(run, 'p1')] = values.get(this.pycnometerRunKey(run, 'p1')) ?? null;
        outputParameters[this.pycnometerRunKey(run, 'p2')] = values.get(this.pycnometerRunKey(run, 'p2')) ?? null;
      }

      return {
        inputParameters: {},
        outputParameters
      };
    }

    if (alias === 'electricalStability') {
      const inputParameters = this.collectParameters(testKey, row.label);
      const outputParameters: Record<string, string | null> = {};
      const readingIndices = this.electricalStabilityReadingIndices(testKey);

      for (const index of readingIndices) {
        const voltage1Key = this.electricalStabilityFieldKey(index, 'voltage1');
        const voltage2Key = this.electricalStabilityFieldKey(index, 'voltage2');
        const voltage3Key = this.electricalStabilityFieldKey(index, 'voltage3');

        if (values.has(voltage1Key)) {
          outputParameters[voltage1Key] = values.get(voltage1Key) ?? null;
        }

        if (values.has(voltage2Key)) {
          outputParameters[voltage2Key] = values.get(voltage2Key) ?? null;
        }

        if (values.has(voltage3Key)) {
          outputParameters[voltage3Key] = values.get(voltage3Key) ?? null;
        }
      }

      const firstIndex = readingIndices[0];
      if (firstIndex !== undefined) {
        const firstVoltage1Key = this.electricalStabilityFieldKey(firstIndex, 'voltage1');
        const firstVoltage2Key = this.electricalStabilityFieldKey(firstIndex, 'voltage2');
        const firstVoltage3Key = this.electricalStabilityFieldKey(firstIndex, 'voltage3');

        if (values.has(firstVoltage1Key)) {
          outputParameters['voltage1'] = values.get(firstVoltage1Key) ?? null;
        }

        if (values.has(firstVoltage2Key)) {
          outputParameters['voltage2'] = values.get(firstVoltage2Key) ?? null;
        }

        if (values.has(firstVoltage3Key)) {
          outputParameters['voltage3'] = values.get(firstVoltage3Key) ?? null;
        }
      }

      outputParameters['equipmentName'] = values.get('equipmentName') ?? null;

      return {
        inputParameters,
        outputParameters
      };
    }

    if (row.customTestDefinitionJson) {
      try {
        const definition = JSON.parse(row.customTestDefinitionJson) as {
          inputParameters: Array<{ name: string; key?: string | null }>;
          outputParameters: Array<{ name: string; key?: string | null; formula?: string | null }>;
          spreadsheet?: Worksheet | null;
          table?: {
            name: string;
            key?: string | null;
            rowCount: number;
            columnCount?: number;
            columns: Array<{ name: string; key?: string | null; kind: 'input' | 'output'; formula?: string | null }>;
          } | null;
        };

        if (definition.spreadsheet) {
          const spreadsheetParameters = this.collectSpreadsheetParameters(testKey, definition.spreadsheet);
          return {
            inputParameters: spreadsheetParameters.inputParameters,
            outputParameters: spreadsheetParameters.outputParameters,
          };
        }

        const inputParameters: Record<string, string | null> = {};
        const inputValueMap: Record<string, string> = {};
        for (const param of definition.inputParameters ?? []) {
          const key = (param.key ?? '').trim() || param.name;
          const val = values.get(key) ?? '';
          inputParameters[key] = val || null;
          inputValueMap[key] = val;
        }

        const outputParameters: Record<string, string | null> = {};
        for (const param of definition.outputParameters ?? []) {
          const key = (param.key ?? '').trim() || param.name;
          const formula = (param.formula ?? '').trim();
          if (!formula) {
            outputParameters[key] = values.get(key) ?? null;
            continue;
          }

          const computed = evaluateCustomFormula(formula, inputValueMap);
          outputParameters[key] = computed || null;
        }

        if (definition.table && definition.table.columns.length > 0) {
          const tableKey = (definition.table.key ?? '').trim() || definition.table.name;
          const rowCount = Math.max(0, Math.floor(definition.table.rowCount || 0));
          const computedOutputValues: Record<string, string> = {};

          const getTableCellValue = (rowNumber: number, columnKey: string): string => {
            const cellKey = `${tableKey}__r${rowNumber}__${columnKey}`;
            if (Object.prototype.hasOwnProperty.call(computedOutputValues, cellKey)) {
              return computedOutputValues[cellKey] ?? '';
            }

            return values.get(cellKey) ?? '';
          };

          const buildTableColumnSums = (): Record<string, string> => {
            const sums: Record<string, string> = {};
            for (const column of definition.table.columns) {
              const columnKey = (column.key ?? '').trim() || column.name;
              let sum = 0;
              for (let rowNumber = 1; rowNumber <= rowCount; rowNumber++) {
                const numericValue = Number(getTableCellValue(rowNumber, columnKey));
                if (Number.isFinite(numericValue)) {
                  sum += numericValue;
                }
              }

              sums[`${columnKey}_sum`] = String(sum);
            }

            return sums;
          };

          for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
            const rowInputValues: Record<string, string> = {};

            for (const column of definition.table.columns) {
              const columnKey = (column.key ?? '').trim() || column.name;
              const cellKey = `${tableKey}__r${rowIndex + 1}__${columnKey}`;
              const cellValue = values.get(cellKey) ?? '';

              if (column.kind === 'input') {
                inputParameters[cellKey] = cellValue || null;
                rowInputValues[columnKey] = cellValue;
              }
            }

            for (const column of definition.table.columns) {
              if (column.kind !== 'output') continue;

              const columnKey = (column.key ?? '').trim() || column.name;
              const cellKey = `${tableKey}__r${rowIndex + 1}__${columnKey}`;
              const formula = (column.formula ?? '').trim();

              if (!formula) {
                const manualValue = values.get(cellKey) ?? null;
                outputParameters[cellKey] = manualValue;
                if (manualValue) {
                  computedOutputValues[cellKey] = manualValue;
                }
                continue;
              }

              const tableColumnSums = buildTableColumnSums();
              const computed = evaluateCustomFormula(formula, { ...tableColumnSums, ...rowInputValues });
              outputParameters[cellKey] = computed || null;
              if (computed) {
                computedOutputValues[cellKey] = computed;
              }
            }
          }
        }

        return { inputParameters, outputParameters };
      } catch {
        // fall through to generic handler
      }
    }
    
    if (alias === 'staticAging') {
      const inputParameters: Record<string, string | null> = {
        temperature: values.get('temperature') ?? null,
        pressure: values.get('pressure') ?? null,
        duration: values.get('duration') ?? null
      };
      const outputParameters: Record<string, string | null> = {};
      const computedFieldByOutputKey: Record<string, string> = {
        initialDensity: 'staticAging_mudDensityPpg',
        topDensity: 'staticAging_topDensity',
        middleDensity: 'staticAging_middleDensity',
        bottomDensity: 'staticAging_bottomDensity',
        asaCombinedDensity: 'staticAging_asaCombinedDensity'
      };
      for (const key of ['initialDensity', 'mudVolume', 'columnMass', 'shelfMass', 'weightsMass',
        'penetration', 'freeFluid', 'topDensity', 'middleDensity', 'bottomDensity',
        'asaCombinedDensity']) {
        const computedFieldKey = computedFieldByOutputKey[key];
        outputParameters[key] = computedFieldKey
          ? this.computedFieldValue(testKey, computedFieldKey) || null
          : values.get(key) ?? null;
      }
      return { inputParameters, outputParameters };
    }

    if (alias === 'psd') {
      const parameters = this.collectParameters(testKey, row.label);

      return {
        inputParameters: parameters,
        outputParameters: { ...parameters }
      };
    }

    if (alias === 'wbmTitration') {
      const inputParameters = this.collectParameters(testKey, row.label);
      const diluentTitratedVolume = values.get('diluentTitratedVolume') ?? values.get('volumeOfDiluentTitratedMl') ?? null;
      const flaskVolume = values.get('flaskVolume') ?? values.get('volumeOfFlaskMl') ?? null;
      const bariteLbPerBbl = values.get('bariteLbPerBbl') ?? values.get('bariteSharePoundsPerBarrel') ?? null;

      const outputParameters: Record<string, string | null> = {
        mudSampleWeight: values.get('mudSampleWeight') ?? null,
        agNo3Volume: values.get('agNo3Volume')
          ?? values.get('wholeMudSilverNitrateVolume')
          ?? values.get('silverNitrateVolume')
          ?? null,
        agNo3Concentration: values.get('agNo3Concentration') ?? null,
        diluentTitratedVolume,
        flaskVolume,
        bariteLbPerBbl,
        chloridesInBlend: this.computedFieldValue(testKey, 'wbmTitration_chloridesInBlend') || null,
        totalChloridesGml: this.computedFieldValue(testKey, 'wbmTitration_totalChlorides') || null,
        volumeOfDiluentTitratedMl: diluentTitratedVolume,
        volumeOfFlaskMl: flaskVolume,
        bariteSharePoundsPerBarrel: bariteLbPerBbl,
        equipmentName: values.get('equipmentName') ?? null
      };

      return {
        inputParameters,
        outputParameters
      };
    }

    return {
      inputParameters: this.collectParameters(testKey, row.label),
      outputParameters: null
    };
  }

  private resolveTestAlias(row: OverviewRowDto): string | null {
    if (row.customTestDefinitionJson) {
      return null;
    }

    const byTypeAlias = row.testTypeAlias?.trim();
    if (byTypeAlias) {
      const mapped = this.testAlias(byTypeAlias);
      if (mapped) return mapped;
    }

    return this.testAlias(row.label);
  }

  private mudCheckMudWeightSpecificGravity(excludedTestKey: string): number {
    return this.mudCheckMudWeightSpecificGravityResult(excludedTestKey).specificGravity;
  }

  private mudCheckMudWeightSpecificGravityResult(excludedTestKey: string): MudCheckSpecificGravityResult {
    const candidates: MudCheckSpecificGravityCandidate[] = [];
    const nameCounts = new Map<'Density' | 'Pycnometer', number>();

    for (let groupIndex = 0; groupIndex < this.overviewGroups.length; groupIndex++) {
      const group = this.overviewGroups[groupIndex];

      for (let overviewIndex = 0; overviewIndex < group.overviews.length; overviewIndex++) {
        const overview = group.overviews[overviewIndex];
        if (overview.type !== 'Tests') continue;

        for (let rowIndex = 0; rowIndex < overview.rows.length; rowIndex++) {
          const candidateTestKey = this.testKey(groupIndex, overviewIndex, rowIndex);
          if (candidateTestKey === excludedTestKey) continue;

          const candidateName = this.mudCheckSpecificGravityTestName(overview.rows[rowIndex]);
          if (!candidateName) continue;

          candidates.push({ testKey: candidateTestKey, name: candidateName });
          nameCounts.set(candidateName, (nameCounts.get(candidateName) ?? 0) + 1);
        }
      }
    }

    const duplicateNames = Array.from(nameCounts.entries())
      .filter(([, count]) => count > 1)
      .map(([name]) => name);

    if (duplicateNames.length > 0) {
      return { specificGravity: 0, duplicateNames };
    }

    for (const candidate of candidates) {
      if (candidate.name === 'Pycnometer') {
        const pycnometerSpecificGravity = Number(this.pycnometerAverageSg(candidate.testKey));
        if (Number.isFinite(pycnometerSpecificGravity) && pycnometerSpecificGravity > 0) {
          return { specificGravity: pycnometerSpecificGravity, duplicateNames: [] };
        }
        continue;
      }

      const candidateValues = this.valueMap(candidate.testKey);
      const densitySpecificGravity = this.firstFinitePositive(
        this.safeNumber(candidateValues.get('measuredSpecificGravity')),
        this.safeNumber(candidateValues.get('specificGravity'))
      );
      if (densitySpecificGravity > 0) {
        return { specificGravity: densitySpecificGravity, duplicateNames: [] };
      }
    }

    return { specificGravity: 0, duplicateNames: [] };
  }

  private firstRetortVolumePercent(phase: 'naf' | 'water'): number | null {
    for (let groupIndex = 0; groupIndex < this.overviewGroups.length; groupIndex++) {
      const group = this.overviewGroups[groupIndex];

      for (let overviewIndex = 0; overviewIndex < group.overviews.length; overviewIndex++) {
        const overview = group.overviews[overviewIndex];
        if (overview.type !== 'Tests') continue;

        for (let rowIndex = 0; rowIndex < overview.rows.length; rowIndex++) {
          if (this.resolveTestAlias(overview.rows[rowIndex]) !== 'retort') continue;

          const retortTestKey = this.testKey(groupIndex, overviewIndex, rowIndex);
          const retortValues = this.valueMap(retortTestKey);
          const cellMudWeight = this.finiteNumberOrNull(retortValues.get('cellMudWeight'));
          const cellTareWeight = this.finiteNumberOrNull(retortValues.get('cellTareWeight'));
          const waterVolume = this.finiteNumberOrNull(retortValues.get('waterVolume'));
          if (cellMudWeight === null || cellTareWeight === null || waterVolume === null) return null;

          const totalVolume = phase === 'naf'
            ? this.finiteNumberOrNull(retortValues.get('totalVolume'))
            : null;
          if (phase === 'naf' && totalVolume === null) return null;

          const mudSpecificGravity = this.mudCheckMudWeightSpecificGravity(retortTestKey);
          if (mudSpecificGravity <= 0) return null;

          const mudVolume = (cellMudWeight - cellTareWeight) / mudSpecificGravity;
          const phaseVolume = phase === 'water'
            ? waterVolume
            : Math.max((totalVolume ?? 0) - waterVolume, 0);
          return mudVolume > 0 ? (100 * phaseVolume) / mudVolume : null;
        }
      }
    }

    return null;
  }

  private firstConstantsOverviewValue(label: string): number | null {
    const constantsOverview = this.overviewGroups
      .flatMap(group => group.overviews)
      .find(overview => overview.type.trim().toLowerCase() === 'constants');
    const normalizedLabel = label.trim().replace(/\s+/g, ' ').toLowerCase();
    const value = constantsOverview?.rows.find(row =>
      row.label.trim().replace(/\s+/g, ' ').toLowerCase() === normalizedLabel
    )?.value;

    return this.finiteNumberOrNull(value);
  }

  private mudCheckSpecificGravityTestName(row: OverviewRowDto): 'Density' | 'Pycnometer' | null {
    const normalizedLabel = row.label.trim().replace(/\s+/g, ' ').toLowerCase();
    if (normalizedLabel === 'density') return 'Density';
    if (normalizedLabel === 'pycnometer') return 'Pycnometer';
    return null;
  }

  private staticAgingNamedDensity(excludedTestKey: string, sourceNames: readonly string[]): number {
    const normalizedSourceNames = new Set(
      sourceNames.map(sourceName => sourceName.trim().replace(/\s+/g, ' ').toLowerCase())
    );

    for (let groupIndex = 0; groupIndex < this.overviewGroups.length; groupIndex++) {
      const group = this.overviewGroups[groupIndex];

      for (let overviewIndex = 0; overviewIndex < group.overviews.length; overviewIndex++) {
        const overview = group.overviews[overviewIndex];
        if (overview.type !== 'Tests') continue;

        for (let rowIndex = 0; rowIndex < overview.rows.length; rowIndex++) {
          const candidateTestKey = this.testKey(groupIndex, overviewIndex, rowIndex);
          if (candidateTestKey === excludedTestKey) continue;

          const candidateRow = overview.rows[rowIndex];
          const normalizedLabel = candidateRow.label.trim().replace(/\s+/g, ' ').toLowerCase();
          if (!normalizedSourceNames.has(normalizedLabel)) continue;

          const candidateAlias = this.resolveTestAlias(candidateRow);
          if (candidateAlias === 'pycnometer') {
            const pycnometerDensity = Number(this.pycnometerAverageDensity(candidateTestKey));
            if (Number.isFinite(pycnometerDensity) && pycnometerDensity > 0) {
              return pycnometerDensity;
            }
            continue;
          }

          if (candidateAlias === 'density') {
            const candidateValues = this.valueMap(candidateTestKey);
            const specificGravity = this.firstFinitePositive(
              this.safeNumber(candidateValues.get('measuredSpecificGravity')),
              this.safeNumber(candidateValues.get('specificGravity'))
            );
            if (specificGravity > 0) {
              return specificGravity * this.waterDensityPpg;
            }
          }
        }
      }
    }

    return 0;
  }

  private numericValue(testKey: string, fieldKey: string): number | null {
    const value = this.valueMap(testKey).get(fieldKey);
    if (!value) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private ensureRheologyTemperatures(testKey: string): number[] {
    const existing = this.rheologyTemperaturesByRow.get(testKey);
    if (existing && existing.length > 0) {
      return existing;
    }

    const defaults = [...this.defaultRheologyTemperatures];
    this.rheologyTemperaturesByRow.set(testKey, defaults);
    return defaults;
  }

  private rheologyTemperatureKeySuffixes(): string[] {
    return ['pressure', 'f2Spring', ...this.rheologyRows.map(row => row.key)];
  }

  private ensureHphtRheologyTemperatures(testKey: string): HphtRheologyReadingView[] {
    const existing = this.hphtRheologyTemperaturesByRow.get(testKey);
    if (existing && existing.length > 0) {
      return existing;
    }

    const defaults = this.defaultHphtRheologyTemperatures.map((temperature, index) => ({ id: index + 1, temperature }));
    this.hphtRheologyTemperaturesByRow.set(testKey, defaults);
    return defaults;
  }

  private hphtRheologyTemperatureKeySuffixes(): string[] {
    return ['temperature', 'pressure', ...this.hphtRheologyRows.map(row => row.key)];
  }

  private extractHphtRheologyReadings(parameters: Record<string, string | null>): HphtRheologyReadingView[] {
    const indexedReadings = Object.entries(parameters).flatMap(([key, value]) => {
      const match = /^reading:(\d+):temperature$/i.exec(key);
      const temperature = Number(value);
      return match && value !== null && Number.isFinite(temperature)
        ? [{ id: Number(match[1]), temperature }]
        : [];
    });
    if (indexedReadings.length > 0) {
      return indexedReadings.sort((a, b) => a.id - b.id);
    }

    const legacyTemperatures = new Set<number>();
    for (const key of Object.keys(parameters)) {
      const match = /^t(\d+)_/i.exec(key);
      if (match) {
        legacyTemperatures.add(Number(match[1]));
      }
    }

    return Array.from(legacyTemperatures)
      .sort((a, b) => a - b)
      .map((temperature, index) => ({ id: index + 1, temperature }));
  }

  private pycnometerRunKey(run: number, reading: 'p1' | 'p2'): string {
    return `run${run}_${reading}`;
  }

  private extractRheologyTemperatures(parameters: Record<string, string | null>): number[] {
    const temperatures = new Set<number>();

    for (const key of Object.keys(parameters)) {
      const match = /^t(\d+)_/i.exec(key);
      if (match) {
        temperatures.add(Number(match[1]));
      }
    }

    for (const [key, value] of Object.entries(parameters)) {
      if (!key.startsWith('reading:') || !key.endsWith(':temperature') || !value) continue;
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        temperatures.add(parsed);
      }
    }

    return Array.from(temperatures).sort((a, b) => a - b);
  }

  private hasAnyPptParameters(parameters: Record<string, string | null>): boolean {
    const pptKeys = ['discGrade', 'temperature', 'pressure', 'filtrateVolume', 'totalVolume', 'cakeThickness'];
    return pptKeys.some(key => {
      const value = parameters[key];
      return value !== null && value !== undefined && value !== '';
    });
  }

  private ensureElectricalStabilityReadings(testKey: string): ElectricalStabilityReadingState[] {
    const existing = this.electricalStabilityReadingsByRow.get(testKey);
    if (existing && existing.length > 0) {
      return existing;
    }

    const defaults = this.defaultElectricalStabilityTemperatures.map((temperature, idx) => ({
      index: idx + 1,
      temperature
    }));

    const values = this.valueMap(testKey);
    for (const reading of defaults) {
      const temperatureKey = this.electricalStabilityFieldKey(reading.index, 'temperature');
      if (!values.has(temperatureKey)) {
        values.set(temperatureKey, String(reading.temperature));
      }

      const idKey = this.electricalStabilityFieldKey(reading.index, 'id');
      if (!values.has(idKey)) {
        values.set(idKey, this.createElectricalStabilityReadingId());
      }
    }

    this.electricalStabilityReadingsByRow.set(testKey, defaults);
    return defaults;
  }

  private extractElectricalStabilityReadings(parameters: Record<string, string | null>): ElectricalStabilityReadingState[] {
    const indices = new Set<number>();

    for (const key of Object.keys(parameters)) {
      const tempMatch = /^esReading(\d+)Temperature$/i.exec(key);
      if (tempMatch) {
        indices.add(Number(tempMatch[1]));
      }

      const idMatch = /^esReading(\d+)Id$/i.exec(key);
      if (idMatch) {
        indices.add(Number(idMatch[1]));
      }
    }

    const hasLegacyTemperature = !!parameters['requestedTemperature'];
    if (indices.size === 0) {
      indices.add(1);
      indices.add(2);
    }

    const valuesByIndex = Array.from(indices)
      .filter(Number.isFinite)
      .sort((a, b) => a - b)
      .map(index => {
        const temperatureKey = this.electricalStabilityFieldKey(index, 'temperature');
        const parsed = Number(parameters[temperatureKey]);
        const fallback = this.defaultElectricalStabilityTemperatures[Math.min(index - 1, this.defaultElectricalStabilityTemperatures.length - 1)]
          ?? this.defaultElectricalStabilityTemperatures[0];

        return {
          index,
          temperature: Number.isFinite(parsed) ? Math.round(parsed) : fallback
        } as ElectricalStabilityReadingState;
      });

    if (hasLegacyTemperature && valuesByIndex.length > 0) {
      const parsedLegacy = Number(parameters['requestedTemperature']);
      if (Number.isFinite(parsedLegacy)) {
        valuesByIndex[0] = {
          ...valuesByIndex[0],
          temperature: Math.round(parsedLegacy)
        };
      }
    }

    return valuesByIndex;
  }

  private extractStoredElectricalStabilityReadingIndices(parameters: Record<string, string | null>): number[] {
    const indices = new Set<number>();

    for (const key of Object.keys(parameters)) {
      const tempMatch = /^esReading(\d+)Temperature$/i.exec(key);
      if (tempMatch) {
        indices.add(Number(tempMatch[1]));
      }

      const idMatch = /^esReading(\d+)Id$/i.exec(key);
      if (idMatch) {
        indices.add(Number(idMatch[1]));
      }
    }

    return Array.from(indices)
      .filter(Number.isFinite)
      .sort((a, b) => a - b);
  }

  private electricalStabilityFieldKey(index: number, keyPart: string): string {
    const normalizedPart = keyPart.charAt(0).toUpperCase() + keyPart.slice(1);
    return `esReading${index}${normalizedPart}`;
  }

  private createElectricalStabilityReadingId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.floor(Math.random() * 100000)}`;
  }

  private loadPycnometerProfiles(): void {
    this.pycnometerProfilesLoading = true;
    this.pycnometerProfilesError = '';

    this.pycnometerProfilesService.getList().subscribe({
      next: profiles => {
        this.pycnometerProfiles = profiles;
        this.pycnometerProfilesLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.pycnometerProfilesError = 'Unable to load pycnometer profiles.';
        this.pycnometerProfilesLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  protected isDownloadingAttachment(attachmentId: number): boolean {
    return this.downloadingAttachments.has(attachmentId);
  }

  protected downloadTestAttachment(attachment: AttachmentDto): void {
    if (this.downloadingAttachments.has(attachment.id)) return;

    this.downloadingAttachments.add(attachment.id);
    this.cdr.markForCheck();

    this.attachmentService.download(attachment.id, AttachmentType.Test).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = attachment.fileName;
        anchor.click();
        URL.revokeObjectURL(url);
        this.downloadingAttachments.delete(attachment.id);
        this.cdr.markForCheck();
      },
      error: () => {
        this.downloadingAttachments.delete(attachment.id);
        this.cdr.markForCheck();
      }
    });
  }

  protected openAttachmentModal(row: OverviewRowDto, groupIndex: number, overviewIndex: number, rowIndex: number): void {
    if (!row.testInputId) {
      this.attachmentActionError = 'Cannot attach to unsaved test.';
      return;
    }

    this.attachmentModalOpen = true;
    this.attachmentModalTestInputId = row.testInputId;
    this.selectedAttachmentFile = null;
    this.attachmentActionError = '';
  }

  protected closeAttachmentModal(): void {
    if (this.uploadingAttachment) {
      return;
    }

    this.attachmentModalOpen = false;
    this.attachmentModalTestInputId = null;
    this.selectedAttachmentFile = null;
    this.attachmentActionError = '';
  }

  protected onAttachmentFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedAttachmentFile = input.files[0];
    }
  }

  protected confirmUploadAttachment(): void {
    if (!this.selectedAttachmentFile || !this.attachmentModalTestInputId || this.uploadingAttachment) {
      return;
    }

    this.uploadingAttachment = true;
    this.attachmentActionError = '';

    this.attachmentService.upload(
      this.attachmentModalTestInputId,
      AttachmentType.Test,
      this.selectedAttachmentFile
    ).subscribe({
      next: () => {
        this.uploadingAttachment = false;
        this.attachmentModalOpen = false;
        this.attachmentModalTestInputId = null;
        this.selectedAttachmentFile = null;
        this.changed.emit();
        this.cdr.markForCheck();
      },
      error: () => {
        this.attachmentActionError = 'Failed to upload attachment.';
        this.uploadingAttachment = false;
        this.cdr.markForCheck();
      }
    });
  }
}
